import React, { useEffect, useState, useRef } from 'react';
import { Auth } from 'aws-amplify';
import { ChildProfile } from '../models/childProfile';
import { StaffProfile } from '../models/staffProfile';
import { useAppState } from '../context/AppStateContext';
import { ChildProfileDataService } from '../services/childProfileDataService';
import { FileDataService } from '../services/fileDataService';
import { UserDataService } from '../services/userDataService';
import { removeBackgroundHeuristic, trimAndCentre, extractBrandColour } from '../services/logoProcessingService';
import { OrgConfig } from '../models/orgConfig';
import { staffColour } from '../utils/staffUtils';

import SidebarNav from '../components/SidebarNav';
import ChildAvatar from '../components/ChildAvatar';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import ConfirmationModal from '../components/ConfirmationModal';
import ChildWhitelistModal from '../components/ChildWhitelistModal';
import { LayoutOutletContext } from '../components/Layout';
import TopTabs from '../components/common/TopTabs';
import { useUrlTab } from '../hooks/useUrlTab';
import ReportTemplateBuilder from '../components/admin/ReportTemplateBuilder/ReportTemplateBuilder';
import LeaversPanel from '../components/admin/LeaversPanel';
import SupportAccessPanel from '../components/admin/SupportAccessPanel';
import MarkAsLeftModal from '../components/admin/MarkAsLeftModal';

/** The five Admin tabs, in the order the strip shows them. */
type AdminTab = 'children' | 'leavers' | 'organisation' | 'staff' | 'reportTemplates';

const ORG_LOGO_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ORG_LOGO_MAX_SIZE = 5 * 1024 * 1024;

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MIN_DIMENSION = 100;

const CHILD_COLOUR_PALETTE = [
    '#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6',
    '#1abc9c', '#e67e22', '#e91e63', '#00bcd4', '#8bc34a',
    '#ff5722', '#673ab7', '#009688', '#ffc107', '#607d8b',
    '#c0392b', '#2980b9', '#27ae60', '#d35400', '#7d3c98',
];

const pickUnusedColour = (existingProfiles: ChildProfile[]): string => {
    const used = new Set(existingProfiles.map(c => c.colour_code?.toLowerCase()));
    const unused = CHILD_COLOUR_PALETTE.find(c => !used.has(c.toLowerCase()));
    return unused ?? CHILD_COLOUR_PALETTE[existingProfiles.length % CHILD_COLOUR_PALETTE.length];
};

type OrgSettingsPayload = Awaited<ReturnType<typeof UserDataService.fetchOrgSettings>>;

/**
 * Baselines for the Organisation tab's dirty check. The tab is fed by two
 * independent endpoints, so each half gets its own snapshot — one failing to
 * load must not leave the other looking edited.
 *
 * logoUrl is deliberately absent: it's a presigned URL that rotates on every
 * refetch, so comparing it would report a change nobody made. The logo's real
 * unsaved state is pendingOrgLogoFile.
 */
interface OrgSettingsSnapshot {
    name: string;
    email: string;
    brandColour: string | null;
    inactivityTimeout: number;
}

interface OrgConfigSnapshot {
    settingName: string;
    sessionInterval: number;
    includeWeekends: boolean;
    workingDayStart: string;
    workingDayEnd: string;
    attendanceGrace: number;
    retentionAnchor: 'leaving' | 'dob25';
    retentionYears: number;
    deletionNotifyEmails: string;
}

/**
 * Draft shape for editing/creating a staff user in the admin panel.
 * id=0 ⇒ new user flow.
 */
interface StaffDraft {
    id: number;
    name: string;
    email: string;
    isActive: boolean;
    isExternal: boolean;
    roleIds: number[];
    childWhitelist: number[];
    /** Custom avatar colour (#rrggbb), or null to use the id-indexed default. */
    colourCode: string | null;
}

const Admin: React.FC = () => {
    const { childProfiles, setChildProfiles, setUserRoleIds, roles, setRoles, setUser, selectedOrganisation, setSelectedOrganisation, activeUser, setIdleTimeoutMs } = useAppState();
    // Rustle It support session: child management and support access itself
    // are hidden; the API's allow-list is what actually enforces it.
    const isSupportSession = activeUser?.isSupport === true;
    const navigate = useNavigate();
    const { sidebarOpen } = useOutletContext<LayoutOutletContext>();

    const [activeTab, setActiveTab] = useUrlTab<AdminTab>(
        'tab',
        ['children', 'leavers', 'organisation', 'staff', 'reportTemplates'],
        'organisation',
    );
    // null = not loaded yet. The Leavers tab stays hidden until we know there
    // is something to show, so it never flashes in and out on load.
    const [leaverCount, setLeaverCount] = useState<number | null>(null);
    // Declared up here (rather than beside the other panel state) because the
    // Organisation scroll-spy effect below reads it in its dependency array.
    const [isInitializing, setIsInitializing] = useState(true);

    // Staff tab state
    const [staffList, setStaffList] = useState<StaffProfile[]>([]);
    const [staffLoading, setStaffLoading] = useState(false);
    const [staffSearch, setStaffSearch] = useState('');
    const [staffStatusFilter, setStaffStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');

    // Currently-edited staff member. `null` ⇒ no panel. `{ id: 0, ... }` ⇒ new user flow.
    const [editStaff, setEditStaff] = useState<StaffDraft | null>(null);
    const [staffSaving, setStaffSaving] = useState(false);
    const [staffStatusMessage, setStaffStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    // Snapshot of editStaff at panel open — used to compute dirty state.
    const [editStaffOriginal, setEditStaffOriginal] = useState<StaffDraft | null>(null);

    // Resend invite (for users still in FORCE_CHANGE_PASSWORD state).
    const [resendInviteSaving, setResendInviteSaving] = useState(false);
    const [resendInviteMessage, setResendInviteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // The whitelist is edited in its own modal — the staff panel only summarises
    // it. See ChildWhitelistModal.
    const [whitelistModalOpen, setWhitelistModalOpen] = useState(false);
    // Tracks whether the staff panel was open in the prior render — suppresses the
    // slide-in animation when switching from one staff member to another.
    const editStaffWasOpen = useRef(false);

    // Org settings state
    const [orgName, setOrgName] = useState('');
    const [orgEmail, setOrgEmail] = useState('');
    const [orgLogoUrl, setOrgLogoUrl] = useState<string | null>(null);
    const [orgInactivityTimeout, setOrgInactivityTimeout] = useState(300); // seconds
    const [pendingOrgLogoFile, setPendingOrgLogoFile] = useState<File | null>(null);
    const [pendingOrgLogoPreview, setPendingOrgLogoPreview] = useState<string | null>(null);
    const [orgLogoError, setOrgLogoError] = useState<string | null>(null);
    const [isSavingOrg, setIsSavingOrg] = useState(false);
    const [orgStatusMessage, setOrgStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const orgLogoInputRef = useRef<HTMLInputElement>(null);
    const logoRetryRef = useRef(0);
    const [orgBrandColour, setOrgBrandColour] = useState<string | null>(null);
    const [isProcessingLogo, setIsProcessingLogo] = useState(false);
    const [pendingOrgLogoOriginal, setPendingOrgLogoOriginal] = useState<File | null>(null);
    const [pendingOrgLogoProcessed, setPendingOrgLogoProcessed] = useState<File | null>(null);
    const [orgLogoMode, setOrgLogoMode] = useState<'clean' | 'original'>('clean');
    const [orgLogoImgError, setOrgLogoImgError] = useState(false);
    // Reset the "image failed to load" flag whenever the shown logo changes.
    useEffect(() => { setOrgLogoImgError(false); }, [pendingOrgLogoPreview, orgLogoUrl]);

    // Org config state — schedule + attendance defaults + report Setting label.
    // Backed by data.organisation_config (migration 014).
    const [orgSettingName, setOrgSettingName] = useState<string>('');
    const [orgSessionInterval, setOrgSessionInterval] = useState(30);
    const [orgIncludeWeekends, setOrgIncludeWeekends] = useState(false);
    const [orgWorkingDayStart, setOrgWorkingDayStart] = useState('08:00');
    const [orgWorkingDayEnd, setOrgWorkingDayEnd] = useState('18:00');
    const [orgAttendanceGrace, setOrgAttendanceGrace] = useState(5);

    // Child-data retention policy (child-deletion epic, migration 037).
    const [orgRetentionAnchor, setOrgRetentionAnchor] = useState<'leaving' | 'dob25'>('leaving');
    const [orgRetentionYears, setOrgRetentionYears] = useState(6);
    const [orgDeletionNotifyEmails, setOrgDeletionNotifyEmails] = useState('');

    // What the server last told us the org looked like. null = not loaded yet,
    // in which case that half simply can't be dirty.
    const [orgSettingsOriginal, setOrgSettingsOriginal] = useState<OrgSettingsSnapshot | null>(null);
    const [orgConfigOriginal, setOrgConfigOriginal] = useState<OrgConfigSnapshot | null>(null);

    // The org form loads from two places — page init and the refetch after a
    // save. Normalising each payload into a snapshot here, then driving the
    // form fields from that same snapshot, is what stops the displayed values
    // and the dirty baseline from drifting apart.
    const applyOrgSettings = (org: OrgSettingsPayload) => {
        const snap: OrgSettingsSnapshot = {
            name: org.name || '',
            email: org.email || '',
            brandColour: org.brandColour || null,
            inactivityTimeout: org.inactivityTimeoutSeconds ?? 300,
        };
        setOrgName(snap.name);
        setOrgEmail(snap.email);
        setOrgBrandColour(snap.brandColour);
        setOrgInactivityTimeout(snap.inactivityTimeout);
        setOrgLogoUrl(org.logoUrl || null);
        setOrgSettingsOriginal(snap);
    };

    const applyOrgConfig = (c: OrgConfig) => {
        const snap: OrgConfigSnapshot = {
            settingName: c.settingName ?? '',
            sessionInterval: c.defaultSessionIntervalMinutes,
            includeWeekends: c.includeWeekends,
            workingDayStart: c.workingDayStart,
            workingDayEnd: c.workingDayEnd,
            attendanceGrace: c.attendanceGraceMinutes,
            retentionAnchor: c.retentionAnchor ?? 'leaving',
            retentionYears: c.retentionYearsAfterLeaving ?? 6,
            deletionNotifyEmails: c.deletionNotifyEmails ?? '',
        };
        setOrgSettingName(snap.settingName);
        setOrgSessionInterval(snap.sessionInterval);
        setOrgIncludeWeekends(snap.includeWeekends);
        setOrgWorkingDayStart(snap.workingDayStart);
        setOrgWorkingDayEnd(snap.workingDayEnd);
        setOrgAttendanceGrace(snap.attendanceGrace);
        setOrgRetentionAnchor(snap.retentionAnchor);
        setOrgRetentionYears(snap.retentionYears);
        setOrgDeletionNotifyEmails(snap.deletionNotifyEmails);
        setOrgConfigOriginal(snap);
    };

    // Sub-tab inside the Organisation panel: General | Schedule | Reporting |
    // Data Retention. The Save button at the bottom persists every section
    // regardless of which sub-tab is currently visible.
    type OrgSubTab = 'general' | 'schedule' | 'reporting' | 'retention' | 'support';
    const [activeOrgSubTab, setActiveOrgSubTab] = useState<OrgSubTab>('general');

    // Refs for the Organisation settings scroll-spy. All three config sections
    // live on one scrollable pane; the left rail jumps to a section on click and
    // tracks the visible section on scroll (highlighting the matching rail item).
    const orgScrollRef = useRef<HTMLDivElement>(null);
    const orgGeneralRef = useRef<HTMLDivElement>(null);
    const orgScheduleRef = useRef<HTMLDivElement>(null);
    const orgReportingRef = useRef<HTMLDivElement>(null);
    const orgRetentionRef = useRef<HTMLDivElement>(null);
    const orgSupportRef = useRef<HTMLDivElement>(null);
    const orgSectionRefs: Record<OrgSubTab, React.RefObject<HTMLDivElement | null>> = {
        general: orgGeneralRef,
        schedule: orgScheduleRef,
        reporting: orgReportingRef,
        retention: orgRetentionRef,
        support: orgSupportRef,
    };

    // Smooth-scroll the pane to a section (rail click). Honours reduced-motion.
    const scrollOrgTo = (id: OrgSubTab) => {
        setActiveOrgSubTab(id);
        const el = orgSectionRefs[id].current;
        const container = orgScrollRef.current;
        if (!el || !container) return;
        const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 12;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        container.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
    };

    // Deep link to a settings section: ?tab=organisation&section=support (the
    // header's support pill). Waits for the Support Access panel to finish
    // loading — it is the last section, so until it has its full height the
    // pane cannot scroll far enough and stops at Data Retention. Then drops
    // the param so the next click on the pill scrolls again from this page.
    const [searchParams, setSearchParams] = useSearchParams();
    const sectionParam = searchParams.get('section');
    const [supportPanelLoaded, setSupportPanelLoaded] = useState(false);
    useEffect(() => {
        if (activeTab !== 'organisation' || isInitializing || sectionParam !== 'support') return;
        const go = () => {
            // It is the last section, so a short one can't scroll to the top
            // of the pane — there is nothing below it to scroll into. Give it
            // a pane's height of room first, so it lands at the top rather
            // than at the bottom of the screen under Data Retention.
            const container = orgScrollRef.current;
            const wrapper = orgSupportRef.current?.parentElement;
            if (container && wrapper) wrapper.style.minHeight = `${container.clientHeight}px`;
            scrollOrgTo('support');
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.delete('section');
                return next;
            }, { replace: true });
        };
        if (supportPanelLoaded) {
            // One frame for the loaded panel to lay out.
            requestAnimationFrame(go);
            return;
        }
        // Fallback if the panel never reports (e.g. its load failed).
        const timer = window.setTimeout(go, 2000);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, isInitializing, sectionParam, supportPanelLoaded]);

    // Scroll-spy: mark the rail item for whichever section currently sits at the
    // top of the pane. Snaps to the last section once scrolled to the bottom so
    // short trailing sections still light up.
    useEffect(() => {
        if (activeTab !== 'organisation') return;
        const container = orgScrollRef.current;
        if (!container) return;
        const order: OrgSubTab[] = ['general', 'schedule', 'reporting', 'retention', 'support'];
        const onScroll = () => {
            const containerTop = container.getBoundingClientRect().top;
            let current: OrgSubTab = 'general';
            for (const id of order) {
                const el = orgSectionRefs[id].current;
                if (el && el.getBoundingClientRect().top - containerTop <= 96) current = id;
            }
            if (container.scrollTop + container.clientHeight >= container.scrollHeight - 4) {
                current = order[order.length - 1];
            }
            setActiveOrgSubTab(prev => (prev === current ? prev : current));
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => container.removeEventListener('scroll', onScroll);
        // isInitializing matters now that Organisation is the tab Admin opens on:
        // the whole tree is behind a loading spinner on mount, so the pane this
        // listens to doesn't exist on the first pass. Without this dep the effect
        // would bail on the null ref and never re-run.
    }, [activeTab, isInitializing]);

    const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
    const [editChild, setEditChild] = useState<ChildProfile | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [isReloading, setIsReloading] = useState(false);
    // Tracks whether the child panel was open in the prior render — suppresses the
    // slide-in animation when switching from one child to another.
    const editChildWasOpen = useRef(false);

    // Grid-view filters (children tab). All / active / inactive + free-text search.
    const [childSearch, setChildSearch] = useState('');
    const [childStatusFilter, setChildStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');

    // Mark-as-left (soft delete) modal — targets the child open in the edit panel.
    const [showMarkAsLeft, setShowMarkAsLeft] = useState(false);

    // Active/inactive is a lifecycle state, not a form field: it's shown as a
    // badge on the record and changed by a named action that applies straight
    // away. `pendingStatus` holds the confirm-dialog request.
    const [pendingStatus, setPendingStatus] = useState<{ kind: 'child' | 'staff'; next: boolean } | null>(null);
    const [statusSaving, setStatusSaving] = useState(false);

    useEffect(() => {
        if (statusMessage?.type === 'success') {
            const t = setTimeout(() => setStatusMessage(null), 3000);
            return () => clearTimeout(t);
        }
    }, [statusMessage]);

    // Track open/closed state of the editor panels so we can suppress the slide-in
    // animation when the user switches between records while the panel is already open.
    useEffect(() => { editChildWasOpen.current = !!editChild; }, [editChild]);
    useEffect(() => { editStaffWasOpen.current = !!editStaff; }, [editStaff]);

    useEffect(() => {
        if (orgStatusMessage?.type === 'success') {
            const t = setTimeout(() => setOrgStatusMessage(null), 3000);
            return () => clearTimeout(t);
        }
    }, [orgStatusMessage]);

    useEffect(() => {
        if (staffStatusMessage?.type === 'success') {
            const t = setTimeout(() => setStaffStatusMessage(null), 3000);
            return () => clearTimeout(t);
        }
    }, [staffStatusMessage]);

    // Profile image state
    const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
    const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);
    const [removeImage, setRemoveImage] = useState(false);
    const [imageError, setImageError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Dirty-state tracking: is the child form different from what was loaded?
    const isChildDirty = (): boolean => {
        if (!editChild) return false;
        if (pendingImageFile || removeImage) return true;
        if (editChild.id === 0) return true; // new unsaved child
        const original = childProfiles.find(c => c.id === editChild.id);
        if (!original) return false;
        return (
            editChild.initials !== original.initials ||
            editChild.first_name !== original.first_name ||
            editChild.last_name !== original.last_name ||
            (editChild.alias || '') !== (original.alias || '') ||
            editChild.colour_code !== original.colour_code ||
            editChild.is_active !== original.is_active ||
            (editChild.dob || null) !== (original.dob || null)
        );
    };

    // Has the Organisation form moved away from what the server gave us? Each
    // half is checked only once its snapshot exists, so a slow or failed load
    // reads as "not dirty" rather than "everything changed".
    const isOrgDirty = (): boolean => {
        if (pendingOrgLogoFile) return true;
        if (orgSettingsOriginal && (
            orgName !== orgSettingsOriginal.name ||
            orgEmail !== orgSettingsOriginal.email ||
            orgBrandColour !== orgSettingsOriginal.brandColour ||
            orgInactivityTimeout !== orgSettingsOriginal.inactivityTimeout
        )) return true;
        if (orgConfigOriginal && (
            orgSettingName !== orgConfigOriginal.settingName ||
            orgSessionInterval !== orgConfigOriginal.sessionInterval ||
            orgIncludeWeekends !== orgConfigOriginal.includeWeekends ||
            orgWorkingDayStart !== orgConfigOriginal.workingDayStart ||
            orgWorkingDayEnd !== orgConfigOriginal.workingDayEnd ||
            orgAttendanceGrace !== orgConfigOriginal.attendanceGrace ||
            orgRetentionAnchor !== orgConfigOriginal.retentionAnchor ||
            orgRetentionYears !== orgConfigOriginal.retentionYears ||
            orgDeletionNotifyEmails !== orgConfigOriginal.deletionNotifyEmails
        )) return true;
        return false;
    };

    /**
     * Unsaved work anywhere on the page. Only for guards that leave the page
     * altogether — switching tabs deliberately doesn't consult this, because
     * every panel's state lives up here in Admin and survives the switch, so
     * there'd be nothing to discard. (isStaffDirty is defined further down
     * with the rest of the staff-panel handlers.)
     */
    const hasUnsavedWork = (): boolean => isChildDirty() || isStaffDirty() || isOrgDirty();

    // Discard confirmation modal state
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const pendingActionRef = useRef<(() => void) | null>(null);

    const runGuarded = (dirty: boolean, action: () => void) => {
        if (!dirty) {
            action();
            return;
        }
        pendingActionRef.current = action;
        setShowDiscardModal(true);
    };

    /** Guards actions that destroy the child form — panel close, switching child. */
    const guardedAction = (action: () => void) => runGuarded(isChildDirty(), action);

    /** Guards actions that leave the Admin page, losing every panel at once. */
    const guardedNavigation = (action: () => void) => runGuarded(hasUnsavedWork(), action);

    /**
     * Throw away the in-progress child edit, restoring the pristine profile
     * (or dropping the draft entirely, for an unsaved new child).
     *
     * Confirming the discard has to do this itself. Most pending actions
     * happen to clear the form as a side effect — closeEdit nulls editChild,
     * openChild moves selectedChildId so the effect below reloads from
     * childProfiles — but any action that doesn't would otherwise leave the
     * edits sitting there after the user asked to lose them.
     */
    const discardChildEdit = () => {
        if (!editChild) return;
        const original = editChild.id === 0
            ? null
            : childProfiles.find(c => c.id === editChild.id) ?? null;
        setEditChild(original ? { ...original } : null);
        if (!original) setSelectedChildId(null);
        clearImageState();
    };

    const handleDiscardConfirm = () => {
        setShowDiscardModal(false);
        discardChildEdit();
        if (pendingActionRef.current) {
            pendingActionRef.current();
            pendingActionRef.current = null;
        }
    };

    const handleDiscardCancel = () => {
        setShowDiscardModal(false);
        pendingActionRef.current = null;
    };

    // Sidebar links leave the page entirely, taking every open panel with
    // them — otherwise a half-finished edit vanishes on a stray click. Every
    // destination is treated the same, so the ViewMode the path maps to
    // doesn't matter here.
    const handleSidebarNavigate = (path: string) => {
        guardedNavigation(() => navigate(path));
    };

    // Applies the status change on its own, immediately. Both paths send ONLY
    // the status flag — any unsaved edits elsewhere in the form stay unsaved
    // rather than being smuggled out by this action — and update the grid row
    // and the editing snapshot together, so an applied change never reads back
    // as an unsaved edit.
    const applyChildActive = async (next: boolean) => {
        if (!editChild || editChild.id === 0) return;
        setStatusSaving(true);
        try {
            const original = childProfiles.find(c => c.id === editChild.id);
            await ChildProfileDataService.updateChildProfile({ ...(original ?? editChild), is_active: next });
            setEditChild(c => (c ? { ...c, is_active: next } : c));
            // Context setter takes an array, not an updater.
            setChildProfiles(childProfiles.map(c => (c.id === editChild.id ? { ...c, is_active: next } : c)));
            setStatusMessage({ type: 'success', text: next ? 'Child reactivated.' : 'Child deactivated.' });
        } catch (err) {
            console.error('Failed to change child status', err);
            setStatusMessage({ type: 'error', text: 'Could not change the status. Please try again.' });
        } finally {
            setStatusSaving(false);
            setPendingStatus(null);
        }
    };

    const applyStaffActive = async (next: boolean) => {
        if (!editStaff || editStaff.id === 0) return;
        setStatusSaving(true);
        try {
            await UserDataService.updateUser(editStaff.id, { isActive: next });
            setEditStaff(s => (s ? { ...s, isActive: next } : s));
            setEditStaffOriginal(o => (o ? { ...o, isActive: next } : o));
            setStaffList(list => list.map(s => (s.id === editStaff.id ? { ...s, isActive: next } : s)));
            setStaffStatusMessage({ type: 'success', text: next ? 'User reactivated.' : 'User deactivated.' });
        } catch (err: any) {
            console.error('Failed to change user status', err);
            // Pass the API's reason through: the last-Admin and self-disable
            // guards are refusals, not glitches, and "please try again" invites
            // the admin to retry something that can never succeed.
            setStaffStatusMessage({
                type: 'error',
                text: err?.response?.data?.error || 'Could not change the status. Please try again.',
            });
        } finally {
            setStatusSaving(false);
            setPendingStatus(null);
        }
    };

    const fetchChildProfiles = async () => {
        setIsReloading(true);
        try {
            // Admin grid wants archived/inactive children too so they can be
            // surfaced under the Inactive / All filters and re-activated.
            const data = await ChildProfileDataService.getChildProfiles(true);
            const sorted = data.slice().sort((a, b) =>
                (a.initials || '').toLowerCase().localeCompare((b.initials || '').toLowerCase())
            );
            setChildProfiles(sorted);
        } catch (err) {
            console.error("Failed to reload children", err);
        } finally {
            setIsReloading(false);
        }
    };

    // Drives whether the Leavers tab is offered at all — a setting that has
    // never had a child leave shouldn't carry a tab about it. Refreshed
    // whenever the leaver population can have changed (mark-as-left, restore,
    // permanent deletion), not polled.
    const refreshLeaverCount = async () => {
        try {
            setLeaverCount(await ChildProfileDataService.getDeletedCount());
        } catch (err) {
            // Never block the page on this: on failure keep the last known
            // count, so the tab doesn't vanish under someone already using it.
            console.error('Failed to load the leaver count', err);
        }
    };

    // Users are loaded whenever that tab is the active one — including on
    // arrival, which is how /admin?tab=staff (a link, a refresh, or a help
    // step) reaches it. This used to hang off the tab strip's click handler,
    // so a deep link showed an empty list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (activeTab === 'staff') void fetchStaffList(); }, [activeTab]);

    const fetchStaffList = async () => {
        setStaffLoading(true);
        try {
            // Admin grid wants disabled users too so they can be filtered and re-enabled.
            const data = await UserDataService.fetchStaffWithRoles(true);
            setStaffList(data);
        } catch (err) {
            console.error('Failed to load staff with roles', err);
        } finally {
            setStaffLoading(false);
        }
    };

    const fetchUserProfile = async (email: string) => {
        try {
            const data = await UserDataService.fetchUserProfile(email);
            setUser(data);
            return data;
        } catch (err) {
            console.error('Error fetching user profile:', err);
            return null;
        }
    };

    // Check for admin access and load data
    useEffect(() => {
        const init = async () => {
            try {
                // Use roles from context if already loaded (e.g. after visiting Home), otherwise fetch
                let rolesMap = roles;
                if (Object.keys(rolesMap).length === 0) {
                    rolesMap = await UserDataService.fetchRolesMap();
                    setRoles(rolesMap);
                }

                const roleIds = activeUser?.roleIds ?? [];
                setUserRoleIds(roleIds);

                if (!roleIds.includes(rolesMap.Admin)) {
                    navigate('/');
                    return;
                }

                // Admin access confirmed — show the page immediately
                setIsInitializing(false);

                // Load remaining data in the background without blocking the render
                // A support session skips what it may not call: the API would
                // refuse each one and log it, in the organisation's activity
                // view, as support having tried.
                if (isSupportSession && activeUser) {
                    setUser({ id: activeUser.id, name: activeUser.name, email: activeUser.email });
                }
                if (!isSupportSession) {
                    Auth.currentAuthenticatedUser()
                        .then(currentUser => {
                            if (currentUser?.attributes?.email) fetchUserProfile(currentUser.attributes.email);
                        })
                        .catch(() => {});
                    fetchChildProfiles();
                    refreshLeaverCount();
                }

                UserDataService.fetchOrgSettings()
                    .then(applyOrgSettings)
                    .catch(err => console.error('Failed to load org settings', err));

                UserDataService.fetchOrgConfig()
                    .then(applyOrgConfig)
                    .catch(err => console.error('Failed to load org config', err));

            } catch (err) {
                console.error('Admin page init error:', err);
                navigate('/');
            }
        };

        init();
    }, []);

    // Warn on browser close/refresh with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedWork()) {
                e.preventDefault();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    });

    useEffect(() => {
        setStatusMessage(null);
    }, [selectedChildId]);

    useEffect(() => {
        if (selectedChildId) {
            const child = childProfiles.find(c => c.id.toString() === selectedChildId);
            if (child) {
                setEditChild({ ...child });
                clearImageState();
            }
        } else {
            setEditChild(null);
            clearImageState();
        }
    }, [selectedChildId, childProfiles]);

    const clearImageState = () => {
        if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
        setPendingImageFile(null);
        setPendingImagePreview(null);
        setRemoveImage(false);
        setImageError(null);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        setImageError(null);
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!ALLOWED_TYPES.includes(file.type)) {
            setImageError('Only JPG, PNG, and WebP images are allowed.');
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
            setImageError('File must be under 5 MB.');
            return;
        }

        // Validate dimensions
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
                setImageError(`Image must be at least ${MIN_DIMENSION}x${MIN_DIMENSION} pixels.`);
                URL.revokeObjectURL(objectUrl);
                return;
            }
            // Valid — set as pending
            if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
            setPendingImageFile(file);
            setPendingImagePreview(objectUrl);
            setRemoveImage(false);
        };
        img.onerror = () => {
            setImageError('Failed to load image. Please try a different file.');
            URL.revokeObjectURL(objectUrl);
        };
        img.src = objectUrl;

        // Reset file input so the same file can be re-selected
        e.target.value = '';
    };

    const handleRemoveImage = () => {
        if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
        setPendingImageFile(null);
        setPendingImagePreview(null);
        setRemoveImage(true);
        setImageError(null);
    };

    const uploadProfileImage = async (childUniqueId: string): Promise<{ s3Key: string; s3KeyThumb: string } | null> => {
        if (!pendingImageFile) return null;

        // Resize: full (400px) + thumbnail (100px)
        const fullBlob = await FileDataService.resizeImage(pendingImageFile, 400, 0.8);
        const thumbBlob = await FileDataService.resizeImage(pendingImageFile, 100, 0.6);

        // Get presigned URLs
        const fullResult = await FileDataService.getPresignedUrl(
            'profile.jpg', 'image/jpeg', childUniqueId, 'profile'
        );
        const thumbResult = await FileDataService.getPresignedUrl(
            'profile_thumb.jpg', 'image/jpeg', childUniqueId, 'profile'
        );

        // Upload both to S3
        await FileDataService.uploadToS3(fullResult.signedUrl, fullBlob);
        await FileDataService.uploadToS3(thumbResult.signedUrl, thumbBlob);

        return { s3Key: fullResult.fileKey, s3KeyThumb: thumbResult.fileKey };
    };

    const handleOrgLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setOrgLogoError(null);
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!ORG_LOGO_ALLOWED_TYPES.includes(file.type)) {
            setOrgLogoError('Only JPG, PNG, and WebP images are allowed.');
            return;
        }
        if (file.size > ORG_LOGO_MAX_SIZE) {
            setOrgLogoError('File must be under 5 MB.');
            return;
        }
        logoRetryRef.current = 0;
        setIsProcessingLogo(true);
        try {
            // Prepare BOTH versions: the original (unchanged) and a cleaned one
            // (background flood-filled out, trimmed + centred). The admin picks
            // which to save — if the auto clean-up looks wrong they keep the original.
            let processedFile: File = file;
            try {
                const processed = await trimAndCentre(await removeBackgroundHeuristic(file));
                processedFile = new File([processed], 'logo.png', { type: 'image/png' });
            } catch (err) {
                console.warn('Background clean-up failed, keeping original', err);
                setOrgLogoError('Could not auto-clean the background — use “Original” if needed.');
            }
            setPendingOrgLogoOriginal(file);
            setPendingOrgLogoProcessed(processedFile);
            setOrgLogoMode('clean');
            if (pendingOrgLogoPreview) URL.revokeObjectURL(pendingOrgLogoPreview);
            setPendingOrgLogoFile(processedFile);
            setPendingOrgLogoPreview(URL.createObjectURL(processedFile));
            // Brand colour from the cleaned version (so the old background doesn't skew it).
            const colour = await extractBrandColour(processedFile);
            if (colour) setOrgBrandColour(colour);
        } finally {
            setIsProcessingLogo(false);
        }
    };

    // Switch between the cleaned and original logo. The selected one is what gets
    // saved, so the choice persists (next session loads exactly what was stored).
    const selectLogoMode = (mode: 'clean' | 'original') => {
        const target = mode === 'original' ? pendingOrgLogoOriginal : pendingOrgLogoProcessed;
        if (!target) return;
        setOrgLogoMode(mode);
        if (pendingOrgLogoPreview) URL.revokeObjectURL(pendingOrgLogoPreview);
        setPendingOrgLogoFile(target);
        setPendingOrgLogoPreview(URL.createObjectURL(target));
    };

    const handleSaveOrg = async (e: React.FormEvent) => {
        e.preventDefault();
        // Reject obviously invalid working-day window before we even hit the
        // network — DB CHECK constraint would also catch it but a friendlier
        // inline error reads better.
        if (orgWorkingDayEnd <= orgWorkingDayStart) {
            setOrgStatusMessage({ type: 'error', text: 'Working day end must be after the start.' });
            return;
        }
        setIsSavingOrg(true);
        setOrgStatusMessage(null);
        try {
            let logoS3Key: string | null | undefined = undefined; // undefined = don't change

            if (pendingOrgLogoFile) {
                const slug = selectedOrganisation?.slug || String(selectedOrganisation?.id || 'org');
                // Preserve PNG/WebP to keep transparency; fall back to JPEG for other formats
                const srcType = pendingOrgLogoFile.type;
                const outMime = (srcType === 'image/png' || srcType === 'image/webp') ? srcType : 'image/jpeg';
                const ext = outMime === 'image/png' ? 'png' : outMime === 'image/webp' ? 'webp' : 'jpg';
                const resized = await FileDataService.resizeImage(pendingOrgLogoFile, 400, 0.85, outMime);
                const { signedUrl, fileKey } = await FileDataService.getPresignedUrl(
                    `logo.${ext}`, outMime, slug, 'org-logo'
                );
                await FileDataService.uploadToS3(signedUrl, resized);
                logoS3Key = fileKey;
            }

            await UserDataService.updateOrgSettings({
                name: orgName,
                email: orgEmail || null,
                inactivityTimeoutSeconds: orgInactivityTimeout,
                brandColourCode: orgBrandColour,
                ...(logoS3Key !== undefined ? { logoS3Key } : {}),
            });

            // Persist feature config alongside the identity settings. Two
            // endpoints rather than one combined call so audit trails stay
            // separate (security.organisation vs data.organisation_config).
            await UserDataService.updateOrgConfig({
                defaultSessionIntervalMinutes: orgSessionInterval,
                includeWeekends: orgIncludeWeekends,
                workingDayStart: orgWorkingDayStart,
                workingDayEnd: orgWorkingDayEnd,
                attendanceGraceMinutes: orgAttendanceGrace,
                settingName: orgSettingName.trim() || null,
                retentionAnchor: orgRetentionAnchor,
                retentionYearsAfterLeaving: orgRetentionYears,
                deletionNotifyEmails: orgDeletionNotifyEmails.trim() || null,
            });

            // Refresh the org settings to get the fresh presigned URL
            const updated = await UserDataService.fetchOrgSettings();
            applyOrgSettings(updated);
            setIdleTimeoutMs((updated.inactivityTimeoutSeconds ?? 300) * 1000);

            // Pull config back so the form mirrors whatever the server stored
            // (in case any field was normalised on the backend).
            const updatedConfig = await UserDataService.fetchOrgConfig();
            applyOrgConfig(updatedConfig);
            if (pendingOrgLogoPreview) URL.revokeObjectURL(pendingOrgLogoPreview);
            setPendingOrgLogoFile(null);
            setPendingOrgLogoPreview(null);
            setPendingOrgLogoOriginal(null);
            setPendingOrgLogoProcessed(null);
            setOrgLogoMode('clean');

            // Update context so Home.tsx picks up the new timeout immediately
            if (selectedOrganisation) {
                setSelectedOrganisation({ ...selectedOrganisation, name: updated.name, email: updated.email, logoUrl: updated.logoUrl, brandColour: updated.brandColour, inactivityTimeoutSeconds: updated.inactivityTimeoutSeconds ?? 300 });
            }

            setOrgStatusMessage({ type: 'success', text: 'Organisation updated successfully!' });
        } catch (err) {
            console.error('Failed to save org settings', err);
            // The API's own message when it gave one — in a support session it
            // says which field support can't change.
            const apiMessage = (err as any)?.response?.data?.error;
            setOrgStatusMessage({ type: 'error', text: apiMessage || 'Failed to save organisation settings.' });
        } finally {
            setIsSavingOrg(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editChild) return;

        setIsSaving(true);
        setStatusMessage(null);

        try {
            let savedChild: ChildProfile;

            if (editChild.id === 0) {
                // Step 1: Create child first (to get unique_id)
                savedChild = await ChildProfileDataService.createChildProfile(editChild);

                // Step 2: Upload image if pending, then update child with S3 keys
                if (pendingImageFile && savedChild.unique_id) {
                    try {
                        const keys = await uploadProfileImage(savedChild.unique_id);
                        if (keys) {
                            savedChild = {
                                ...savedChild,
                                profile_image_s3_key: keys.s3Key,
                                profile_image_s3_key_thumbnail: keys.s3KeyThumb,
                            };
                            await ChildProfileDataService.updateChildProfile(savedChild);
                        }
                    } catch (imgErr) {
                        console.error('Profile image upload failed:', imgErr);
                        // Child was created, image just didn't upload
                    }
                }

                const newList = [...childProfiles, savedChild].sort((a, b) =>
                    (a.initials || '').toLowerCase().localeCompare((b.initials || '').toLowerCase())
                );
                setChildProfiles(newList);
                setStatusMessage({ type: 'success', text: 'Child profile created successfully!' });
                setSelectedChildId(savedChild.id.toString());
                setEditChild(savedChild);

            } else {
                // Existing child — handle image upload/removal
                let childToSave = { ...editChild };

                if (pendingImageFile && editChild.unique_id) {
                    try {
                        const keys = await uploadProfileImage(editChild.unique_id);
                        if (keys) {
                            childToSave.profile_image_s3_key = keys.s3Key;
                            childToSave.profile_image_s3_key_thumbnail = keys.s3KeyThumb;
                        }
                    } catch (imgErr) {
                        console.error('Profile image upload failed:', imgErr);
                        setStatusMessage({ type: 'error', text: 'Profile saved but image upload failed.' });
                    }
                } else if (removeImage) {
                    childToSave.profile_image_s3_key = null;
                    childToSave.profile_image_s3_key_thumbnail = null;
                }

                await ChildProfileDataService.updateChildProfile(childToSave);
                savedChild = childToSave;

                setChildProfiles(childProfiles.map(c => c.id === savedChild.id ? savedChild : c));
                if (!statusMessage || statusMessage.type !== 'error') {
                    setStatusMessage({ type: 'success', text: 'Child profile updated successfully!' });
                }
            }

            clearImageState();
            // Reload to get fresh presigned URLs
            await fetchChildProfiles();

        } catch (err: any) {
            console.error("Error saving child:", err);
            // A plan limit is not a failure — it is an answer, and it has a
            // remedy the admin can act on. Surface the server's message rather
            // than flattening it into "Failed to save", which would leave them
            // with no idea whether to retry, delete a child, or call us.
            const data = err?.response?.data;
            setStatusMessage({
                type: 'error',
                text: data?.code === 'child_cap_reached'
                    ? data.error
                    : 'Failed to save child profile.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (field: keyof ChildProfile, value: any) => {
        if (!editChild) return;
        const updated = { ...editChild, [field]: value };
        if (field === 'first_name' || field === 'last_name') {
            const first = (field === 'first_name' ? value : editChild.first_name) || '';
            const last = (field === 'last_name' ? value : editChild.last_name) || '';
            if (last) {
                updated.initials = (first[0] || '').toUpperCase() + last[0].toUpperCase();
            } else {
                updated.initials = (first[0] || '').toUpperCase();
            }
        }
        setEditChild(updated);
    };

    // ── Staff edit-panel handlers ───────────────────────────────────────────

    /**
     * Why the Admin role (and the account itself) may be locked on the open
     * record. The API enforces all of this in-transaction — see PUT
     * /security/user-roles and PUT /security/user/:id — so this exists purely
     * so the rule is visible *before* Save rather than arriving as a 400.
     *
     * `self` is the active staff profile, not the Cognito login: on a shared
     * device the PIN-switched user is who the API treats as the caller too.
     *
     * The admin count comes from the loaded list and can be stale by seconds.
     * That's fine — this greys a checkbox; the transaction is what guarantees
     * an organisation is never left with nobody who can administer it.
     */
    const staffAdminLock = (() => {
        const adminRoleId = roles.Admin;
        const holdsAdmin = !!adminRoleId && !!editStaff && editStaff.roleIds.includes(adminRoleId);
        const isSelf = !!editStaff && editStaff.id !== 0 && editStaff.id === activeUser?.id;
        const activeAdmins = adminRoleId
            ? staffList.filter(s => s.isActive !== false && s.roleIds.includes(adminRoleId)).length
            : 0;
        // Measured against the SAVED record, not the draft: ticking Admin on a
        // new user must not read as "this is the last admin" and lock the box
        // you just ticked. Only an existing, active, already-Admin record can
        // be the one the organisation would be left without.
        const saved = editStaff && editStaff.id !== 0
            ? staffList.find(s => s.id === editStaff.id)
            : undefined;
        const isSavedActiveAdmin = !!adminRoleId && !!saved
            && saved.isActive !== false && saved.roleIds.includes(adminRoleId);
        const isLastAdmin = isSavedActiveAdmin && activeAdmins <= 1;
        return {
            holdsAdmin,
            isSelf,
            isLastAdmin,
            /** Set when the Admin role cannot be given up on this record. */
            roleReason: !holdsAdmin
                ? null
                : isSelf
                    ? "you can't remove your own Admin role"
                    : isLastAdmin
                        ? 'the only Admin in this organisation'
                        : null,
            /** Set when this record cannot be deactivated. */
            deactivateReason: !editStaff || editStaff.id === 0
                ? null
                : isSelf
                    ? 'You cannot deactivate your own account.'
                    : isLastAdmin
                        ? 'The only Admin in this organisation cannot be deactivated.'
                        : null,
        };
    })();

    const isStaffDirty = (): boolean => {
        if (!editStaff || !editStaffOriginal) return false;
        if (editStaff.id === 0) return true; // new user is always dirty
        if (editStaff.name !== editStaffOriginal.name) return true;
        if (editStaff.isActive !== editStaffOriginal.isActive) return true;
        if (editStaff.isExternal !== editStaffOriginal.isExternal) return true;
        if (editStaff.colourCode !== editStaffOriginal.colourCode) return true;
        if (editStaff.roleIds.slice().sort().join(',') !== editStaffOriginal.roleIds.slice().sort().join(',')) return true;
        if (editStaff.childWhitelist.slice().sort().join(',') !== editStaffOriginal.childWhitelist.slice().sort().join(',')) return true;
        return false;
    };

    const openStaffPanel = async (staff: StaffProfile) => {
        // Fetch the child whitelist alongside opening so the panel renders fully.
        let whitelist: number[] = [];
        try {
            whitelist = await UserDataService.fetchChildWhitelist(staff.id);
        } catch {
            // Non-fatal — admin endpoint may not be available, fall through with empty list.
        }
        const draft: StaffDraft = {
            id: staff.id,
            name: staff.name,
            email: staff.email,
            isActive: staff.isActive !== false,
            isExternal: staff.isExternal === true,
            roleIds: [...staff.roleIds],
            childWhitelist: whitelist,
            colourCode: staff.customColourCode ?? null,
        };
        setEditStaff(draft);
        setEditStaffOriginal(draft);
        setStaffStatusMessage(null);
        setResendInviteSaving(false);
        setResendInviteMessage(null);
        setWhitelistModalOpen(false);
    };

    const startNewStaff = () => {
        const draft: StaffDraft = {
            id: 0,
            name: '',
            email: '',
            isActive: true,
            isExternal: false,
            roleIds: roles.Editor ? [roles.Editor] : [],
            childWhitelist: [],
            colourCode: null,
        };
        setEditStaff(draft);
        setEditStaffOriginal(draft);
        setStaffStatusMessage(null);
        setResendInviteSaving(false);
        setResendInviteMessage(null);
        setWhitelistModalOpen(false);
    };

    const closeStaffPanel = () => {
        setEditStaff(null);
        setEditStaffOriginal(null);
        setStaffStatusMessage(null);
        setResendInviteSaving(false);
        setResendInviteMessage(null);
        setWhitelistModalOpen(false);
    };

    const handleResendInvite = async () => {
        if (!editStaff || editStaff.id === 0) return;
        setResendInviteSaving(true);
        setResendInviteMessage(null);
        try {
            await UserDataService.resendInvite(editStaff.id);
            setResendInviteMessage({ type: 'success', text: `Invitation resent to ${editStaff.email}.` });
            setTimeout(() => setResendInviteMessage(null), 3000);
        } catch (err: any) {
            setResendInviteMessage({ type: 'error', text: err?.response?.data?.error || 'Failed to resend invitation.' });
        } finally {
            setResendInviteSaving(false);
        }
    };



    const updateStaffField = <K extends keyof StaffDraft>(field: K, value: StaffDraft[K]) => {
        setEditStaff(prev => prev ? { ...prev, [field]: value } : prev);
    };

    const toggleStaffRole = (roleId: number) => {
        setEditStaff(prev => {
            if (!prev) return prev;
            const has = prev.roleIds.includes(roleId);
            return { ...prev, roleIds: has ? prev.roleIds.filter(r => r !== roleId) : [...prev.roleIds, roleId] };
        });
    };

    const toggleStaffWhitelistChild = (childId: number) => {
        setEditStaff(prev => {
            if (!prev) return prev;
            const has = prev.childWhitelist.includes(childId);
            return { ...prev, childWhitelist: has ? prev.childWhitelist.filter(c => c !== childId) : [...prev.childWhitelist, childId] };
        });
    };

    const handleSaveStaff = async () => {
        if (!editStaff) return;
        if (editStaff.roleIds.length === 0) {
            setStaffStatusMessage({ type: 'error', text: 'Select at least one role.' });
            return;
        }
        setStaffSaving(true);
        setStaffStatusMessage(null);
        try {
            if (editStaff.id === 0) {
                // New user flow — Cognito + DB create on the backend.
                if (!editStaff.email.trim()) {
                    setStaffStatusMessage({ type: 'error', text: 'Email is required.' });
                    setStaffSaving(false);
                    return;
                }
                const created = await UserDataService.createUser({
                    email: editStaff.email.trim(),
                    name: editStaff.name.trim(),
                    isExternal: editStaff.isExternal,
                    roleIds: editStaff.roleIds,
                    childWhitelist: editStaff.childWhitelist,
                    colourCode: editStaff.colourCode,
                });
                // Only a brand-new account gets Cognito's invitation email — an
                // existing login gets our own "you've been added" notice instead,
                // so don't send the admin chasing an email that was never sent.
                const who = created.name || editStaff.email;
                let statusText: string;
                if (created.outcome === 'created') {
                    statusText = `${who} created. They'll receive an email invitation to set up their account.`;
                } else if (created.invitationResent) {
                    // Their account existed but had never been signed in to, so
                    // they were sent a fresh invitation rather than an "added" note.
                    statusText = `${who} has been added to your organisation. Their account had never been used, so they've been sent a new invitation with a temporary password.`;
                } else {
                    const added = created.outcome === 'linked'
                        ? `${who} already has a Rustle It account and has been added to your organisation. They'll sign in with their existing details.`
                        : `${who} has been re-enabled in your organisation, with the roles and access you've just set.`;
                    // The membership is committed even if the email failed, so a
                    // failed send is a warning on a success, not an error.
                    statusText = created.notified
                        ? `${added} They've been emailed to let them know.`
                        : `${added} We couldn't send the notification email — please let them know directly.`;
                }
                setStaffStatusMessage({ type: 'success', text: statusText });
                await fetchStaffList();
                // A linked user keeps their existing (global) name, so take the
                // server's value rather than leaving the typed one on screen.
                const saved = { ...editStaff, id: created.id, name: created.name || editStaff.name };
                setEditStaff(saved);
                setEditStaffOriginal(saved);
            } else {
                // Update path — name + active + roles + whitelist in parallel.
                const nameChanged = editStaff.name !== editStaffOriginal?.name;
                const activeChanged = editStaff.isActive !== editStaffOriginal?.isActive;
                const externalChanged = editStaff.isExternal !== editStaffOriginal?.isExternal;
                const colourChanged = editStaff.colourCode !== editStaffOriginal?.colourCode;
                const rolesChanged = editStaff.roleIds.slice().sort().join(',') !== editStaffOriginal?.roleIds.slice().sort().join(',');
                const whitelistChanged = editStaff.childWhitelist.slice().sort().join(',') !== editStaffOriginal?.childWhitelist.slice().sort().join(',');
                await Promise.all([
                    (nameChanged || activeChanged || externalChanged || colourChanged)
                        ? UserDataService.updateUser(editStaff.id, {
                            ...(nameChanged ? { name: editStaff.name } : {}),
                            ...(activeChanged ? { isActive: editStaff.isActive } : {}),
                            ...(externalChanged ? { isExternal: editStaff.isExternal } : {}),
                            ...(colourChanged ? { colourCode: editStaff.colourCode } : {}),
                        })
                        : Promise.resolve(),
                    rolesChanged
                        ? UserDataService.setUserRoles(editStaff.id, editStaff.roleIds)
                        : Promise.resolve(),
                    whitelistChanged
                        ? UserDataService.setUserChildWhitelist(editStaff.id, editStaff.childWhitelist)
                        : Promise.resolve(),
                ]);
                setStaffStatusMessage({ type: 'success', text: 'Saved.' });
                setEditStaffOriginal(editStaff);
                await fetchStaffList();
            }
        } catch (err: any) {
            setStaffStatusMessage({ type: 'error', text: err?.response?.data?.error || 'Failed to save user.' });
        } finally {
            setStaffSaving(false);
        }
    };

    // Determine what image to show in the preview
    const getPreviewImageUrl = (): string | null => {
        if (removeImage) return null;
        if (pendingImagePreview) return pendingImagePreview;
        return editChild?.profile_image_url || null;
    };

    const hasExistingOrPendingImage = (): boolean => {
        if (removeImage) return false;
        return !!(pendingImagePreview || editChild?.profile_image_url);
    };

    if (isInitializing) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
                <div className={`filters-panel ${sidebarOpen ? 'open' : 'is-rail'}`}>
                <div className="sidebar d-flex flex-column">
                        {/* A direct flex child, as on Home: wrapped in a plain
                            div it never stretched, so Help & How Tos — which
                            pushes itself down with margin-top:auto — sat
                            immediately under the last nav item instead of at
                            the foot of the menu. */}
                        <SidebarNav className="mb-2" onNavigate={handleSidebarNavigate} />
                    </div>
                    <div />
                </div>

                {/* Right area: tabs then content */}
                <div className="top-tabs-page" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <TopTabs<'children' | 'leavers' | 'organisation' | 'staff' | 'reportTemplates'>
                        ariaLabel="Admin sections"
                        dataHelp="admin.tabs"
                        active={activeTab}
                        // Tab switches are deliberately unguarded. Every panel's
                        // state lives up here in Admin, so an in-progress child
                        // edit is still sitting there when you come back — there
                        // is nothing to discard, and prompting would offer to
                        // throw away work that was never at risk. The prompts
                        // live on the paths that do destroy it: closing the
                        // panel, opening a different child, and leaving the page.
                        onChange={(id) => {
                            if (id === 'children') {
                                setActiveTab('children');
                            } else if (id === 'leavers') {
                                setActiveTab('leavers');
                            } else if (id === 'staff') {
                                // The fetch lives in an effect, not here: the tab can
                                // now be arrived at by URL as well as by click.
                                setActiveTab('staff');
                            } else if (id === 'reportTemplates') {
                                setActiveTab('reportTemplates');
                            } else {
                                setActiveTab('organisation');
                            }
                        }}
                        // Ordered broadest → narrowest scope: the setting itself, then who
                        // works there, then the children, then the children who left, then
                        // the paperwork. That's also the order you'd set a new organisation
                        // up in, so it doubles as the onboarding path — hence Organisation
                        // is both first and the tab Admin opens on. Leavers stays next to
                        // Child Profiles (a leaver is a child who left).
                        // Leavers is hidden until the setting actually has one —
                        // most orgs never will, and an empty tab about children
                        // leaving is noise. Marking a child as left reveals it
                        // (refreshLeaverCount runs on that path); restoring the
                        // last one keeps it up while it's the tab you're on,
                        // rather than pulling it out from under you.
                        tabs={[
                            { id: 'organisation',    label: 'Organisation',     icon: 'fa-building' },
                            // "Users", not "Staff": the list includes External accounts, who
                            // aren't staff — and "Staff" is now a specific account type inside
                            // the editor, so reusing it here would name two different things.
                            { id: 'staff',           label: 'Users',            icon: 'fa-users' },
                            ...(isSupportSession ? [] : [{ id: 'children' as const, label: 'Child Profiles', icon: 'fa-child' }]),
                            ...(!isSupportSession && ((leaverCount ?? 0) > 0 || activeTab === 'leavers')
                                ? [{ id: 'leavers' as const, label: 'Leavers', icon: 'fa-door-open' }]
                                : []),
                            { id: 'reportTemplates', label: 'Report Templates', icon: 'fa-file-invoice' },
                        ]}
                    />

{activeTab === 'children' && (() => {
                const startNewChild = () => guardedAction(() => {
                    setEditChild({
                        id: 0,
                        initials: '',
                        first_name: '',
                        last_name: '',
                        colour_code: pickUnusedColour(childProfiles),
                        is_active: true,
                    });
                    setSelectedChildId('0');
                    setStatusMessage(null);
                    clearImageState();
                });

                const closeEdit = () => guardedAction(() => {
                    setEditChild(null);
                    setSelectedChildId(null);
                    setStatusMessage(null);
                    clearImageState();
                });

                const openChild = (child: ChildProfile) => {
                    if (String(child.id) === selectedChildId) return;
                    guardedAction(() => {
                        setSelectedChildId(String(child.id));
                        setStatusMessage(null);
                        clearImageState();
                    });
                };

                const q = childSearch.trim().toLowerCase();
                const visibleChildren = childProfiles.filter(c => {
                    if (childStatusFilter === 'active' && !c.is_active) return false;
                    if (childStatusFilter === 'inactive' && c.is_active) return false;
                    if (!q) return true;
                    const haystack = [
                        c.first_name, c.last_name, c.initials, c.alias || '',
                    ].join(' ').toLowerCase();
                    return haystack.includes(q);
                });

                return (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div className="pill-toolbar" role="toolbar" aria-label="Child profile filters">
                        <div className="pill-toolbar__search">
                            <i className="fas fa-search" aria-hidden="true" />
                            <input
                                type="text"
                                placeholder="Search by name, alias or initials"
                                value={childSearch}
                                onChange={e => setChildSearch(e.target.value)}
                                aria-label="Search child profiles"
                            />
                        </div>
                        <div className="pill-toolbar__segment" role="group" aria-label="Filter by status">
                            {(['active', 'all', 'inactive'] as const).map(opt => (
                                <button
                                    key={opt}
                                    type="button"
                                    className={childStatusFilter === opt ? 'is-active' : ''}
                                    onClick={() => setChildStatusFilter(opt)}
                                    aria-pressed={childStatusFilter === opt}
                                >
                                    {opt === 'all' ? 'All' : opt === 'active' ? 'Active' : 'Inactive'}
                                </button>
                            ))}
                        </div>
                        <div className="pill-toolbar__spacer" />
                        <button
                            className="pill-toolbar__icon-btn"
                            onClick={fetchChildProfiles}
                            disabled={isReloading}
                            title="Reload children"
                            aria-label="Reload children"
                        >
                            <i
                                className="fas fa-sync-alt"
                                style={isReloading ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined}
                            />
                        </button>
                    </div>

                    {/* Grid pane + slide-in panel row */}
                    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
                    <div className="children-grid-pane" style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '1.25rem 1.5rem 2rem' }}>
                        <div
                            className="children-grid"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                gap: '1rem',
                            }}
                        >
                            {/* Add-new tile, first slot */}
                            <button
                                type="button"
                                onClick={startNewChild}
                                className="child-card child-card--new"
                                data-help="admin.new-child"
                                aria-label="Add a new child profile"
                            >
                                <div className="child-card__plus">
                                    <i className="fas fa-plus" />
                                </div>
                                <div className="child-card__new-label">New Child</div>
                            </button>

                            {visibleChildren.map(c => {
                                const isSel = String(c.id) === selectedChildId;
                                return (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => openChild(c)}
                                        className={`child-card ${isSel ? 'is-selected' : ''} ${c.is_active ? '' : 'is-inactive'}`}
                                        title={`Edit ${c.first_name} ${c.last_name}`}
                                    >
                                        <ChildAvatar
                                            child={c}
                                            size={88}
                                            // Full-size (400px) image, not the 100px thumbnail — at
                                            // 88px these tiles already outrun the thumbnail on a 2×
                                            // screen. The face-pile avatars elsewhere stay on thumbs.
                                            imageUrl={c.profile_image_url || c.profile_image_thumbnail_url || null}
                                            showBadge={true}
                                        />
                                        <div className="child-card__body">
                                            <div className="child-card__name">{c.first_name} {c.last_name}</div>
                                            {c.alias && <div className="child-card__alias">"{c.alias}"</div>}
                                        </div>
                                        <div className="child-card__meta">
                                            <span className={`child-card__dot ${c.is_active ? 'is-active' : 'is-inactive'}`} aria-hidden="true" />
                                            <span className="child-card__status">{c.is_active ? 'Active' : 'Inactive'}</span>
                                        </div>
                                        <div className="child-card__bar" style={{ background: c.colour_code }} aria-hidden="true" />
                                    </button>
                                );
                            })}

                            {visibleChildren.length === 0 && (
                                <div className="text-muted py-5" style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                                    {q || childStatusFilter !== 'all'
                                        ? 'No children match the current filter.'
                                        : 'No children yet — click + to add the first one.'}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Slide-in detail panel */}
                    {editChild && (
                        <div
                            key={editChild.id}
                            className={`detail-panel card${editChildWasOpen.current ? '' : ' detail-panel-slide'}`}
                            style={{
                                width: 460,
                                minWidth: 460,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <div className="detail-panel-scroll" style={{ flex: 1, overflowY: 'auto', paddingBottom: 80 }}>
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">
                                        {editChild.id === 0 ? 'New Child' : `${editChild.first_name || editChild.initials} ${editChild.last_name || ''}`.trim()}
                                    </h4>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary close-button"
                                        title="Close"
                                        onClick={closeEdit}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>

                                {/* Lifecycle state, not a form field: the badge reports where the
                                    record stands and the verb beside it does one thing, straight
                                    away. Hidden on create — you don't make a deactivated record. */}
                                {editChild.id !== 0 && (
                                    <div className={`record-bar${editChild.is_active ? '' : ' is-off'}`}>
                                        <span className="record-bar__badge">
                                            <span className="record-bar__dot" aria-hidden="true" />
                                            {editChild.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                        <span className="record-bar__note">
                                            {editChild.is_active
                                                ? 'Appears in posts, schedules and reports.'
                                                : 'Hidden from posts, schedules and reports — visible here in Admin.'}
                                        </span>
                                        <button
                                            type="button"
                                            className={`record-bar__action${editChild.is_active ? '' : ' is-restore'}`}
                                            onClick={() => editChild.is_active
                                                ? setPendingStatus({ kind: 'child', next: false })
                                                : applyChildActive(true)}
                                            disabled={statusSaving}
                                        >
                                            {editChild.is_active ? 'Deactivate' : 'Reactivate'}
                                        </button>
                                    </div>
                                )}

                                <div className="card-body">
                                    {statusMessage && (
                                        <div className={`alert alert-${statusMessage.type === 'success' ? 'success' : 'danger'} py-2`}>
                                            {statusMessage.text}
                                        </div>
                                    )}
                                    <form id="child-edit-form" onSubmit={handleSave}>
                                        {/* Profile image */}
                                        <div className="mb-4 d-flex align-items-start gap-4 p-3" style={{ background: 'rgba(0,0,0,0.03)', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)' }}>
                                            {/* The colour sits under the avatar because it *is* the
                                                avatar — ChildAvatar paints the initials on colour_code
                                                whenever there's no photo. It used to be a separate field
                                                further down, so you couldn't see what you were changing. */}
                                            <div className="child-identity">
                                                <ChildAvatar
                                                    child={editChild}
                                                    size={104}
                                                    imageUrl={getPreviewImageUrl()}
                                                    showBadge={true}
                                                />
                                                <div className="child-identity__colour">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={editChild.colour_code}
                                                        onChange={e => handleChange('colour_code', e.target.value)}
                                                        aria-label="Profile colour"
                                                        title="Profile colour"
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={editChild.colour_code}
                                                        onChange={e => handleChange('colour_code', e.target.value)}
                                                        aria-label="Profile colour hex value"
                                                    />
                                                </div>
                                            </div>
                                            <div className="d-flex flex-column gap-2">
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept="image/jpeg,image/png,image/webp"
                                                    onChange={handleFileSelect}
                                                    style={{ display: 'none' }}
                                                    aria-label="Upload profile photo"
                                                />
                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => fileInputRef.current?.click()}>
                                                    <i className="fas fa-camera me-1"></i> Upload photo
                                                </button>
                                                {hasExistingOrPendingImage() && (
                                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleRemoveImage}>
                                                        <i className="fas fa-trash me-1"></i> Remove
                                                    </button>
                                                )}
                                                <small className="text-muted">5 MB max &middot; JPG, PNG, WebP</small>
                                            </div>
                                        </div>
                                        {imageError && (
                                            <div className="text-danger mb-3" style={{ fontSize: '0.85rem' }}>
                                                <i className="fas fa-exclamation-circle me-1"></i>{imageError}
                                            </div>
                                        )}

                                        <div className="row g-2 mb-3">
                                            <div className="col">
                                                <label className="form-label">First name</label>
                                                <input type="text" className="form-control" value={editChild.first_name || ''} onChange={e => handleChange('first_name', e.target.value)} required />
                                            </div>
                                            <div className="col">
                                                <label className="form-label">Last name</label>
                                                <input type="text" className="form-control" value={editChild.last_name || ''} onChange={e => handleChange('last_name', e.target.value)} required />
                                            </div>
                                        </div>

                                        <div className="row g-2 mb-3">
                                            <div className="col-auto">
                                                <label className="form-label">Initials</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={editChild.initials}
                                                    onChange={e => handleChange('initials', e.target.value)}
                                                    maxLength={2}
                                                    readOnly={!!editChild.last_name}
                                                    style={{ width: 80, backgroundColor: editChild.last_name ? 'rgba(0,0,0,0.05)' : undefined }}
                                                    required
                                                />
                                            </div>
                                            <div className="col">
                                                <label className="form-label">Alias <span className="text-muted">(optional)</span></label>
                                                <input type="text" className="form-control" value={editChild.alias || ''} onChange={e => handleChange('alias', e.target.value)} />
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">Date of birth <span className="text-muted">(optional)</span></label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={editChild.dob || ''}
                                                onChange={e => handleChange('dob', e.target.value || null)}
                                                style={{ maxWidth: 220 }}
                                            />
                                            <small className="text-muted">
                                                Stored encrypted. Used only to calculate how long records are retained
                                                after the child leaves.
                                            </small>
                                        </div>

                                        {/* Colour lives in the avatar box above; status is in the bar
                                            under the header. Neither is an ordinary form field. */}
                                    </form>
                                </div>
                            </div>

                            {/* Sticky footer */}
                            <div className="detail-footer-bar d-flex justify-content-end gap-2 px-3 py-3" style={{
                                background: 'linear-gradient(0deg, rgba(250,248,241,0.96), rgba(250,248,241,0.85))',
                                borderTop: '1px solid rgba(38, 146, 63, 0.22)',
                            }}>
                                {editChild.id !== 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger me-auto"
                                        data-help="admin.mark-as-left"
                                        onClick={() => guardedAction(() => setShowMarkAsLeft(true))}
                                        disabled={isSaving}
                                        title="Flag this child as having left the setting (soft delete — undoable from the Leavers tab)"
                                    >
                                        <i className="fas fa-door-open me-1" />Mark as left…
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={closeEdit}
                                    disabled={isSaving}
                                >
                                    {isChildDirty() ? 'Cancel' : 'Close'}
                                </button>
                                <button
                                    type="submit"
                                    form="child-edit-form"
                                    className="btn btn-primary"
                                    data-help="admin.child-save"
                                    disabled={isSaving || (editChild.id !== 0 && !isChildDirty())}
                                >
                                    {isSaving ? 'Saving…' : (editChild.id === 0 ? 'Create child' : 'Save changes')}
                                </button>
                            </div>
                        </div>
                    )}
                    </div>{/* end grid+panel row */}
                </div>
                );
                })()}

                {activeTab === 'staff' && (() => {
                    const q = staffSearch.trim().toLowerCase();
                    const visibleStaff = staffList.filter(s => {
                        const isActive = s.isActive !== false; // undefined ⇒ assume active (legacy)
                        if (staffStatusFilter === 'active' && !isActive) return false;
                        if (staffStatusFilter === 'inactive' && isActive) return false;
                        if (!q) return true;
                        return [s.name, s.email].join(' ').toLowerCase().includes(q);
                    });
                    // isExternal is now a flag on the membership, not a role.
                    const isExternal = !!editStaff && editStaff.isExternal;

                    return (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div className="pill-toolbar" role="toolbar" aria-label="User filters">
                        <div className="pill-toolbar__search">
                            <i className="fas fa-search" aria-hidden="true" />
                            <input
                                type="text"
                                placeholder="Search by name or email"
                                value={staffSearch}
                                onChange={e => setStaffSearch(e.target.value)}
                                aria-label="Search users"
                            />
                        </div>
                        <div className="pill-toolbar__segment" role="group" aria-label="Filter by status">
                            {(['active', 'all', 'inactive'] as const).map(opt => (
                                <button
                                    key={opt}
                                    type="button"
                                    className={staffStatusFilter === opt ? 'is-active' : ''}
                                    onClick={() => setStaffStatusFilter(opt)}
                                    aria-pressed={staffStatusFilter === opt}
                                >
                                    {opt === 'all' ? 'All' : opt === 'active' ? 'Active' : 'Inactive'}
                                </button>
                            ))}
                        </div>
                        <div className="pill-toolbar__spacer" />
                        <button
                            className="pill-toolbar__icon-btn"
                            onClick={fetchStaffList}
                            disabled={staffLoading}
                            title="Reload staff"
                            aria-label="Reload staff"
                        >
                            <i
                                className="fas fa-sync-alt"
                                style={staffLoading ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined}
                            />
                        </button>
                    </div>

                    {/* Grid + slide-in panel */}
                    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
                        <div className="children-grid-pane" style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '1.25rem 1.5rem 2rem' }}>
                            {staffLoading && staffList.length === 0 ? (
                                <div className="d-flex justify-content-center align-items-center py-5">
                                    <div className="spinner-border text-success" role="status" />
                                </div>
                            ) : (
                                <div
                                    className="children-grid"
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                        gap: '1rem',
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={startNewStaff}
                                        data-help="admin.new-user"
                                        className="child-card child-card--new"
                                        aria-label="Add a new user"
                                    >
                                        <div className="child-card__plus">
                                            <i className="fas fa-user-plus" />
                                        </div>
                                        <div className="child-card__new-label">New User</div>
                                    </button>

                                    {visibleStaff.map(s => {
                                        const isSel = !!editStaff && editStaff.id === s.id;
                                        const isActive = s.isActive !== false;
                                        const roleNames = s.roleIds
                                            .map(id => Object.entries(roles).find(([, v]) => v === id)?.[0])
                                            .filter(Boolean) as string[];
                                        return (
                                            <button
                                                key={s.id}
                                                type="button"
                                                onClick={() => openStaffPanel(s)}
                                                className={`child-card ${isSel ? 'is-selected' : ''} ${isActive ? '' : 'is-inactive'}`}
                                                title={`Edit ${s.name}`}
                                            >
                                                <div
                                                    style={{
                                                        width: 76, height: 76, borderRadius: '50%',
                                                        background: s.colourCode,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        color: '#fff', fontWeight: 700, fontSize: '1.4rem',
                                                        boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.18)',
                                                        textShadow: '0 1px 1px rgba(0,0,0,0.25)',
                                                    }}
                                                    aria-hidden="true"
                                                >
                                                    {s.initials}
                                                </div>
                                                <div className="child-card__body">
                                                    <div className="child-card__name">{s.name}</div>
                                                    <div className="child-card__alias" style={{ fontStyle: 'normal' }}>{s.email}</div>
                                                </div>
                                                {(roleNames.length > 0 || s.isExternal) && (
                                                    <div className="d-flex flex-wrap justify-content-center gap-1" style={{ marginTop: 4 }}>
                                                        {s.isExternal && (
                                                            <span
                                                                style={{
                                                                    fontSize: '0.65rem',
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.04em',
                                                                    padding: '2px 8px',
                                                                    borderRadius: 999,
                                                                    background: 'rgba(160, 77, 44, 0.14)',
                                                                    color: '#7a3a1f',
                                                                    fontWeight: 600,
                                                                    border: '1px solid rgba(160, 77, 44, 0.4)',
                                                                }}
                                                            >
                                                                External
                                                            </span>
                                                        )}
                                                        {roleNames.map(n => (
                                                            <span
                                                                key={n}
                                                                style={{
                                                                    fontSize: '0.65rem',
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.04em',
                                                                    padding: '2px 8px',
                                                                    borderRadius: 999,
                                                                    background: 'rgba(38, 146, 63, 0.12)',
                                                                    color: '#0d4d1a',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {n}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className="child-card__meta">
                                                    <span className={`child-card__dot ${isActive ? 'is-active' : 'is-inactive'}`} aria-hidden="true" />
                                                    <span className="child-card__status">{isActive ? 'Active' : 'Inactive'}</span>
                                                </div>
                                                <div className="child-card__bar" style={{ background: s.colourCode }} aria-hidden="true" />
                                            </button>
                                        );
                                    })}

                                    {visibleStaff.length === 0 && !staffLoading && (
                                        <div className="text-muted py-5" style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                                            {q ? 'No users match the current filter.' : 'No staff yet — click + to add the first one.'}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Slide-in detail panel */}
                        {editStaff && (
                            <div
                                key={editStaff.id || 'new'}
                                className={`detail-panel card${editStaffWasOpen.current ? '' : ' detail-panel-slide'}`}
                                style={{ width: 560, minWidth: 560, display: 'flex', flexDirection: 'column' }}
                            >
                                <div className="detail-panel-scroll" style={{ flex: 1, overflowY: 'auto', paddingBottom: 80 }}>
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h4 className="mb-0">{editStaff.id === 0 ? 'New User' : editStaff.name || editStaff.email}</h4>
                                        <button type="button" className="btn btn-sm btn-outline-secondary close-button" title="Close" onClick={closeStaffPanel}>
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>

                                    {/* Same lifecycle bar as the child editor — inactive means
                                        something different here (no access, rather than hidden
                                        from the app), so only the consequence line differs. */}
                                    {editStaff.id !== 0 && (
                                        <div className={`record-bar${editStaff.isActive ? '' : ' is-off'}`}>
                                            <span className="record-bar__badge">
                                                <span className="record-bar__dot" aria-hidden="true" />
                                                {editStaff.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            <span className="record-bar__note">
                                                {/* Not "can sign in": this flag is per-organisation
                                                    membership (user_organisation.is_active), and users can
                                                    belong to several — deactivating here doesn't stop them
                                                    signing in elsewhere. "Access" is also the word the app
                                                    already used ("revoke access without deleting the user").
                                                    While deactivation is barred, the reason replaces the
                                                    consequence — it's the more useful half to read. */}
                                                {!editStaff.isActive
                                                    ? 'Kept on record, but has no access to this organisation.'
                                                    : staffAdminLock.deactivateReason ?? 'Has access to this organisation.'}
                                            </span>
                                            <button
                                                type="button"
                                                className={`record-bar__action${editStaff.isActive ? '' : ' is-restore'}`}
                                                onClick={() => editStaff.isActive
                                                    ? setPendingStatus({ kind: 'staff', next: false })
                                                    : applyStaffActive(true)}
                                                disabled={statusSaving || (editStaff.isActive && !!staffAdminLock.deactivateReason)}
                                                title={editStaff.isActive ? staffAdminLock.deactivateReason ?? undefined : undefined}
                                            >
                                                {editStaff.isActive ? 'Deactivate' : 'Reactivate'}
                                            </button>
                                        </div>
                                    )}

                                    <div className="card-body">
                                        {staffStatusMessage && (
                                            <div className={`alert alert-${staffStatusMessage.type === 'success' ? 'success' : 'danger'} py-2`}>
                                                {staffStatusMessage.text}
                                            </div>
                                        )}

                                        <div className="mb-3">
                                            <label className="form-label">Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={editStaff.name}
                                                onChange={e => updateStaffField('name', e.target.value)}
                                                placeholder="Full name"
                                            />
                                        </div>

                                        {/* Avatar colour — optional. Stored on the user so it stays
                                            stable across environments/devices; null falls back to the
                                            id-indexed default colour. */}
                                        <div className="mb-3">
                                            <label className="form-label" htmlFor="staffColour">Profile colour</label>
                                            <div className="d-flex align-items-center gap-2">
                                                <input
                                                    id="staffColour"
                                                    type="color"
                                                    className="form-control form-control-color"
                                                    value={editStaff.colourCode ?? staffColour(editStaff.id)}
                                                    onChange={e => updateStaffField('colourCode', e.target.value)}
                                                    title="Choose staff colour"
                                                />
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={editStaff.colourCode ?? staffColour(editStaff.id)}
                                                    onChange={e => updateStaffField('colourCode', e.target.value)}
                                                    style={{ maxWidth: 110 }}
                                                />
                                                {editStaff.colourCode === null ? (
                                                    // The one cue that this swatch is derived, not chosen. The
                                                    // reset button carries the opposite state, so a helper line
                                                    // saying the same thing would just be a second label.
                                                    <span className="staff-colour__auto">Automatic</span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => updateStaffField('colourCode', null)}
                                                    >
                                                        Reset to automatic
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">
                                                Email{' '}
                                                {editStaff.id !== 0 && (
                                                    <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.78rem' }}>
                                                        — tied to the Cognito user, cannot be changed
                                                    </span>
                                                )}
                                            </label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                value={editStaff.email}
                                                disabled={editStaff.id !== 0}
                                                onChange={e => updateStaffField('email', e.target.value)}
                                                placeholder="name@example.com"
                                            />

                                            {/* Rare action, so it hangs off the address it acts on rather
                                                than occupying a bordered card of its own. The "only needed
                                                if the invite expired" note moved to the tooltip — the label
                                                already says what the button does. */}
                                            {editStaff.id !== 0 && (
                                                <div className="staff-invite">
                                                    <button
                                                        type="button"
                                                        className="staff-invite__btn"
                                                        onClick={handleResendInvite}
                                                        disabled={resendInviteSaving}
                                                        title="Only needed if the original invitation email expired or was missed"
                                                    >
                                                        <i className="fas fa-envelope" aria-hidden="true" />
                                                        {resendInviteSaving ? 'Sending…' : 'Resend invite'}
                                                    </button>
                                                    {resendInviteMessage && (
                                                        <span className={`staff-invite__msg${resendInviteMessage.type === 'error' ? ' is-error' : ''}`}>
                                                            {resendInviteMessage.text}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {editStaff.id === 0 && (
                                            <div className="mb-3">
                                                <small className="text-muted">
                                                    Cognito will email the new user an invitation with a temporary password. They must set a new password on first sign-in.
                                                </small>
                                            </div>
                                        )}

                                        {/* Status is not a form field — see the bar under the header. */}

                                        {/* ── Access ───────────────────────────────────────────────
                                            Account type, then roles, then scope: the three settings
                                            that decide what this person can see and do. External
                                            used to sit beside Active — an account-status control —
                                            even though it's an access decision, and the invite box
                                            separated it from the Admin role it's mutually exclusive
                                            with, so cause and effect were never on screen together. */}
                                        <div className="staff-access" data-help="admin.access">
                                            <h6 className="staff-access__head">Access</h6>
                                            {(() => {
                                                const adminRoleId = roles.Admin;
                                                const hasAdminRole = !!adminRoleId && editStaff.roleIds.includes(adminRoleId);
                                                const types = [
                                                    { ext: false, name: 'Staff', note: 'Full access to this organisation, as set by their roles.' },
                                                    { ext: true, name: 'External', note: 'Sees only whitelisted children, and can edit only their own posts.' },
                                                ];
                                                return (
                                                    <div className="staff-type" role="radiogroup" aria-label="Account type">
                                                        {types.map(t => {
                                                            const selected = editStaff.isExternal === t.ext;
                                                            // Admin and External are mutually exclusive. Blocking the
                                                            // choice rather than silently dropping Admin keeps a
                                                            // permissions change from happening unnoticed.
                                                            const blocked = t.ext && hasAdminRole;
                                                            return (
                                                                <button
                                                                    key={t.name}
                                                                    type="button"
                                                                    role="radio"
                                                                    aria-checked={selected}
                                                                    disabled={blocked}
                                                                    className={`staff-type__opt${t.ext ? ' staff-type__opt--external' : ''}${selected ? ' is-selected' : ''}`}
                                                                    onClick={() => updateStaffField('isExternal', t.ext)}
                                                                >
                                                                    <span className="staff-type__name">{t.name}</span>
                                                                    <span className="staff-type__note">
                                                                        {blocked
                                                                            ? 'Remove the Admin role first — admins cannot be external.'
                                                                            : t.note}
                                                                    </span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {/* The anchor sits on the group, not the
                                            label: help should ring the heading AND
                                            the checklist it introduces, since the
                                            word "Roles" alone points at nothing the
                                            user can act on. */}
                                        <div className="mb-4" data-help="admin.roles">
                                            <label className="form-label">Roles</label>
                                            <div style={{ background: 'rgba(0,0,0,0.03)', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)', padding: '0.75rem 1rem' }}>
                                                {Object.entries(roles)
                                                    // External is no longer a role — it's the membership-level
                                                    // toggle above. Hide it from the roles checklist.
                                                    .filter(([name]) => name !== 'External')
                                                    .sort(([, a], [, b]) => a - b)
                                                    .map(([name, id]) => {
                                                        // Three reasons the Admin box locks, in the order they're
                                                        // worth reading: External is mutually exclusive with Admin;
                                                        // you can't strip your own Admin role; and the last Admin
                                                        // standing can't be stripped at all. The latter two only
                                                        // bite while the role is *held* — granting is never blocked.
                                                        const lockNote = name !== 'Admin'
                                                            ? null
                                                            : editStaff.isExternal
                                                                ? 'not available for External users'
                                                                : staffAdminLock.roleReason;
                                                        const disabled = !!lockNote;
                                                        return (
                                                        <div key={id} className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`new-role-${id}`}
                                                                checked={editStaff.roleIds.includes(id)}
                                                                onChange={() => toggleStaffRole(id)}
                                                                disabled={disabled}
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor={`new-role-${id}`}
                                                                title={lockNote ?? undefined}
                                                            >
                                                                {name}
                                                                {lockNote && (
                                                                    <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.78rem', marginLeft: 8 }}>
                                                                        — {lockNote}
                                                                    </span>
                                                                )}
                                                            </label>
                                                        </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>

                                        {/* Child whitelist — summary here, editing in a modal. Building a
                                            whitelist is a bulk job (search, scan, add several, review) and
                                            never fitted this 560px panel: the search box, Add all and Clear
                                            fought for one row and the list scrolled inside a scroller. The
                                            panel now answers 'who can they see?' at a glance instead. */}
                                        {(() => {
                                            const selectedSet = new Set(editStaff.childWhitelist);
                                            const chosen = childProfiles
                                                .filter(c => selectedSet.has(c.id))
                                                .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
                                            const count = editStaff.childWhitelist.length;
                                            return (
                                                <div className="mb-4" data-help="admin.whitelist">
                                                    <label className="form-label">Child whitelist</label>
                                                    <div className="cwl-summary">
                                                        {count === 0 ? (
                                                            <p className="cwl-summary__empty">
                                                                {isExternal
                                                                    ? 'Empty — this external user sees no children at all.'
                                                                    : 'Empty — access to every child.'}
                                                            </p>
                                                        ) : (
                                                            <div className="cwl-summary__faces">
                                                                {chosen.slice(0, 6).map(c => (
                                                                    <span key={c.id} className="cwl-summary__face" title={`${c.first_name} ${c.last_name}`}>
                                                                        <ChildAvatar
                                                                            child={c}
                                                                            size={30}
                                                                            imageUrl={c.profile_image_thumbnail_url || c.profile_image_url || null}
                                                                            showBadge={false}
                                                                        />
                                                                    </span>
                                                                ))}
                                                                {count > 6 && <span className="cwl-summary__more">+{count - 6}</span>}
                                                                <span className="cwl-summary__count">
                                                                    {count} {count === 1 ? 'child' : 'children'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary cwl-summary__btn"
                                                            onClick={() => setWhitelistModalOpen(true)}
                                                            disabled={childProfiles.length === 0}
                                                            title={childProfiles.length === 0 ? 'Add children on the Child Profiles tab first' : undefined}
                                                        >
                                                            {count === 0 ? 'Choose children' : 'Edit list'}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Sticky footer */}
                                <div className="detail-footer-bar d-flex justify-content-end gap-2 px-3 py-3" style={{
                                    background: 'linear-gradient(0deg, rgba(250,248,241,0.96), rgba(250,248,241,0.85))',
                                    borderTop: '1px solid rgba(38, 146, 63, 0.22)',
                                }}>
                                    <button type="button" className="btn btn-outline-secondary" onClick={closeStaffPanel} disabled={staffSaving}>
                                        {isStaffDirty() ? 'Cancel' : 'Close'}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        data-help="admin.user-save"
                                        onClick={handleSaveStaff}
                                        disabled={staffSaving || (editStaff.id !== 0 && !isStaffDirty())}
                                    >
                                        {staffSaving ? 'Saving…' : (editStaff.id === 0 ? 'Create user' : 'Save changes')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {whitelistModalOpen && editStaff && (
                        <ChildWhitelistModal
                            staffName={editStaff.name.trim()}
                            childProfiles={childProfiles}
                            value={editStaff.childWhitelist}
                            isExternal={editStaff.isExternal}
                            onApply={(ids) => {
                                updateStaffField('childWhitelist', ids);
                                setWhitelistModalOpen(false);
                            }}
                            onCancel={() => setWhitelistModalOpen(false)}
                        />
                    )}
                </div>
                    );
                })()}

                {activeTab === 'organisation' && (
                <div className="org-settings">
                    {/* Left rail — jumps to a config section and tracks scroll position.
                        Config sections are stacked on one scrollable pane (right),
                        VS-project-properties style, rather than a floating card. */}
                    <nav className="org-settings__rail" aria-label="Organisation settings sections">
                        <div className="org-settings__rail-title">Settings</div>
                        {([
                            { id: 'general' as const,   label: 'General',               icon: 'fa-building' },
                            { id: 'schedule' as const,  label: 'Schedule & Attendance', icon: 'fa-calendar-check' },
                            { id: 'reporting' as const, label: 'Reporting',             icon: 'fa-file-alt' },
                            { id: 'retention' as const, label: 'Data Retention',        icon: 'fa-user-clock' },
                            { id: 'support' as const,   label: 'Support Access',        icon: 'fa-life-ring' },
                        ]).filter(s => !(isSupportSession && s.id === 'support')).map(s => (
                            <button
                                key={s.id}
                                type="button"
                                className={`org-rail-item ${activeOrgSubTab === s.id ? 'is-active' : ''}`}
                                // Only the one a How To points at, for now.
                                data-help={s.id === 'schedule' ? 'admin.org-rail-schedule' : undefined}
                                aria-current={activeOrgSubTab === s.id ? 'true' : undefined}
                                onClick={() => scrollOrgTo(s.id)}
                            >
                                <i className={`fas ${s.icon}`} aria-hidden="true" />
                                <span>{s.label}</span>
                            </button>
                        ))}
                    </nav>

                    <div className="org-settings__pane">
                        <div className="org-settings__scroll" ref={orgScrollRef}>
                            <form id="org-settings-form" className="org-settings__form" onSubmit={handleSaveOrg}>
                                <header className="org-settings__hero">
                                    <h2>Organisation Settings</h2>
                                    <p>Identity, schedule defaults and reporting for {orgName || 'your organisation'}.</p>
                                </header>

                                <section className="org-section" ref={orgGeneralRef} aria-labelledby="org-sec-general">
                                    <div className="org-section__head">
                                        <div className="org-section__head-icon"><i className="fas fa-building" aria-hidden="true" /></div>
                                        <div>
                                            <h3 id="org-sec-general">General</h3>
                                            <p>Your organisation’s name, logo and screen-lock behaviour.</p>
                                        </div>
                                    </div>
                                {/* Logo + brand colour */}
                                <div className="mb-4" data-help="admin.org-logo">
                                    <label className="form-label">Organisation Logo</label>
                                    <div className="p-3" style={{ background: 'rgba(0,0,0,0.03)', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)' }}>
                                        {/* Preview + upload */}
                                        <div className="d-flex align-items-center gap-4">
                                            <div
                                                style={{
                                                    width: 116, height: 116, borderRadius: '50%', padding: 4, flexShrink: 0,
                                                    background: orgBrandColour || '#26923f',
                                                    boxShadow: '0 8px 20px -8px rgba(13,77,26,0.5)',
                                                }}
                                                title="Preview (ring shows the brand colour)"
                                            >
                                                <div style={{
                                                    width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden',
                                                    border: '3px solid #fff',
                                                    background: ((pendingOrgLogoPreview || orgLogoUrl) && !orgLogoImgError) ? '#ffffff' : 'linear-gradient(160deg, #26923f 0%, #106123 100%)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    {((pendingOrgLogoPreview || orgLogoUrl) && !orgLogoImgError) ? (
                                                        <img
                                                            src={pendingOrgLogoPreview || orgLogoUrl!}
                                                            alt="Logo preview"
                                                            onError={() => {
                                                                // Presigned S3 URLs can transiently fail (network / SSO-signed URL).
                                                                // Re-fetch a fresh URL a couple of times before giving up.
                                                                if (!pendingOrgLogoPreview && logoRetryRef.current < 2) {
                                                                    logoRetryRef.current += 1;
                                                                    UserDataService.fetchOrgSettings().then(o => setOrgLogoUrl(o.logoUrl || null)).catch(() => setOrgLogoImgError(true));
                                                                } else {
                                                                    console.warn('Org logo failed to load:', pendingOrgLogoPreview || orgLogoUrl);
                                                                    setOrgLogoImgError(true);
                                                                }
                                                            }}
                                                            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }}
                                                        />
                                                    ) : (
                                                        <span style={{ color: '#fff', fontSize: '2.2rem', fontWeight: 700 }}>{orgName.charAt(0).toUpperCase() || '?'}</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex-grow-1">
                                                <div className="text-muted mb-2" style={{ fontSize: '0.82rem' }}>Shown on reports and in the app. PNG, JPG or WebP &middot; up to 5&nbsp;MB.</div>
                                                <input ref={orgLogoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleOrgLogoSelect} style={{ display: 'none' }} aria-label="Upload org logo" />
                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => orgLogoInputRef.current?.click()} disabled={isProcessingLogo}>
                                                    <i className="fas fa-camera me-1"></i>{(pendingOrgLogoPreview || orgLogoUrl) ? 'Replace logo' : 'Upload logo'}
                                                </button>
                                                {isProcessingLogo && (
                                                    <div className="text-muted mt-2" style={{ fontSize: '0.82rem' }}><i className="fas fa-spinner fa-spin me-1"></i>Cleaning background &amp; reading colour…</div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Background choice — only for a freshly-picked logo */}
                                        {pendingOrgLogoOriginal && !isProcessingLogo && (
                                            <div className="mt-3 pt-3 d-flex align-items-center flex-wrap gap-2" data-help="admin.logo-background" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                                                <span className="text-muted" style={{ fontSize: '0.82rem', minWidth: 92 }}>Background</span>
                                                <div className="btn-group btn-group-sm" role="group" aria-label="Logo version">
                                                    <button type="button" className={`btn ${orgLogoMode === 'clean' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => selectLogoMode('clean')} title="Remove the background so the logo sits cleanly on reports">Clean</button>
                                                    <button type="button" className={`btn ${orgLogoMode === 'original' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => selectLogoMode('original')} title="Keep the logo exactly as uploaded — no changes">Original</button>
                                                </div>
                                                <span className="text-muted" style={{ fontSize: '0.78rem' }}>Not happy with the clean-up? Choose “Original”.</span>
                                            </div>
                                        )}

                                        {/* Brand colour */}
                                        <div className="mt-3 pt-3 d-flex align-items-center flex-wrap gap-2" data-help="admin.brand-colour" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                                            <label className="m-0 text-muted" htmlFor="orgBrandColour" style={{ fontSize: '0.82rem', minWidth: 92 }}>Brand colour</label>
                                            <input id="orgBrandColour" type="color" value={orgBrandColour || '#26923f'} onChange={(e) => setOrgBrandColour(e.target.value)} title="Used to colour reports — auto-picked from the logo; adjust if needed" style={{ width: 44, height: 30, padding: 2, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 6, cursor: 'pointer', background: '#fff' }} />
                                            <code style={{ fontSize: '0.8rem', color: '#555' }}>{(orgBrandColour || '#26923f').toUpperCase()}</code>
                                            <span className="text-muted" style={{ fontSize: '0.78rem' }}>Reports use this colour (auto-picked from the logo).</span>
                                        </div>
                                    </div>
                                    {orgLogoError && (
                                        <div className="text-danger mt-2" style={{ fontSize: '0.85rem' }}>
                                            <i className="fas fa-exclamation-circle me-1"></i>{orgLogoError}
                                        </div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Organisation Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={orgName}
                                        onChange={e => setOrgName(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Contact Email</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={orgEmail}
                                        onChange={e => setOrgEmail(e.target.value)}
                                        placeholder="optional"
                                        disabled={isSupportSession}
                                    />
                                    {isSupportSession && <small className="text-muted">Only the organisation's admins can change this.</small>}
                                </div>
                                <div className="mb-4" data-help="admin.inactivity-timeout">
                                    <label className="form-label d-flex justify-content-between">
                                        <span>Inactivity Lock Timeout</span>
                                        <span className="text-muted" style={{ fontWeight: 400 }}>
                                            {orgInactivityTimeout < 60
                                                ? `${orgInactivityTimeout}s`
                                                : `${Math.floor(orgInactivityTimeout / 60)}m${orgInactivityTimeout % 60 > 0 ? ` ${orgInactivityTimeout % 60}s` : ''}`}
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        className="form-range"
                                        min={5}
                                        max={300}
                                        step={5}
                                        value={orgInactivityTimeout}
                                        onChange={e => setOrgInactivityTimeout(Number(e.target.value))}
                                    />
                                    <div className="d-flex justify-content-between" style={{ fontSize: '0.75rem', color: '#999' }}>
                                        <span>5s</span>
                                        <span>5 min</span>
                                    </div>
                                </div>
                                </section>

                                <section className="org-section" ref={orgScheduleRef} aria-labelledby="org-sec-schedule">
                                    <div className="org-section__head">
                                        <div className="org-section__head-icon"><i className="fas fa-calendar-check" aria-hidden="true" /></div>
                                        <div>
                                            <h3 id="org-sec-schedule">Schedule &amp; Attendance</h3>
                                            <p>Defaults for the weekly schedule grid and how arrivals count as on-time.</p>
                                        </div>
                                    </div>
                                <div className="mb-3" data-help="admin.grid-interval">
                                    <label className="form-label">Schedule grid interval</label>
                                    <select
                                        className="form-select"
                                        value={orgSessionInterval}
                                        onChange={e => setOrgSessionInterval(Number(e.target.value))}
                                    >
                                        {[5, 10, 15, 20, 30, 45, 60, 90, 120].map(m => (
                                            <option key={m} value={m}>{m} minutes</option>
                                        ))}
                                    </select>
                                    <small className="text-muted">
                                        Granularity of the Weekly Schedule grid &mdash;
                                        drag-created sessions snap to this interval.
                                    </small>
                                </div>

                                <div className="mb-3 form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="orgIncludeWeekends"
                                        checked={orgIncludeWeekends}
                                        onChange={e => setOrgIncludeWeekends(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="orgIncludeWeekends">
                                        Show Saturday &amp; Sunday on the schedule grid
                                    </label>
                                </div>

                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <label className="form-label">Working day starts</label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            step={1800}
                                            value={orgWorkingDayStart}
                                            onChange={e => setOrgWorkingDayStart(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Working day ends</label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            step={1800}
                                            value={orgWorkingDayEnd}
                                            onChange={e => setOrgWorkingDayEnd(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="form-label d-flex justify-content-between">
                                        <span>Attendance &ldquo;on-time&rdquo; grace</span>
                                        <span className="text-muted" style={{ fontWeight: 400 }}>
                                            ±{orgAttendanceGrace} min
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        className="form-range"
                                        min={0}
                                        max={60}
                                        step={1}
                                        value={orgAttendanceGrace}
                                        onChange={e => setOrgAttendanceGrace(Number(e.target.value))}
                                    />
                                    <div className="d-flex justify-content-between" style={{ fontSize: '0.75rem', color: '#999' }}>
                                        <span>0 min</span>
                                        <span>60 min</span>
                                    </div>
                                    <small className="text-muted">
                                        Arrivals or departures within this window of the scheduled time read as on-time.
                                    </small>
                                </div>
                                </section>

                                <section className="org-section" ref={orgReportingRef} aria-labelledby="org-sec-reporting">
                                    <div className="org-section__head">
                                        <div className="org-section__head-icon"><i className="fas fa-file-alt" aria-hidden="true" /></div>
                                        <div>
                                            <h3 id="org-sec-reporting">Reporting</h3>
                                            <p>How your organisation appears on generated report PDFs.</p>
                                        </div>
                                    </div>
                                <div className="mb-4">
                                    <label className="form-label">Setting / Location</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={orgSettingName}
                                        onChange={e => setOrgSettingName(e.target.value)}
                                        placeholder={orgName ? `Defaults to "${orgName}"` : 'Defaults to the organisation name'}
                                    />
                                    <small className="text-muted">
                                        Shown as the &ldquo;Setting&rdquo; row on generated report PDFs. Useful when
                                        reports need the full school name or an address that differs from the short
                                        organisation name. Leave blank to use the organisation name as the report header.
                                    </small>
                                </div>

                                {/* Report templates have their own CRUD flow, so they live in a
                                    dedicated top-level tab rather than inside this single-save form. */}
                                <div className="mb-4">
                                    <button
                                        type="button"
                                        className="btn btn-link p-0"
                                        style={{ fontSize: '0.9rem' }}
                                        onClick={() => setActiveTab('reportTemplates')}
                                    >
                                        Manage report templates <i className="fas fa-arrow-right ms-1" />
                                    </button>
                                    <div>
                                        <small className="text-muted">
                                            Define what generated reports contain — timeframe, header fields, and content.
                                        </small>
                                    </div>
                                </div>
                                </section>

                                <section className="org-section" ref={orgRetentionRef} aria-labelledby="org-sec-retention">
                                    <div className="org-section__head">
                                        <div className="org-section__head-icon"><i className="fas fa-user-clock" aria-hidden="true" /></div>
                                        <div>
                                            <h3 id="org-sec-retention">Data Retention</h3>
                                            <p>How long a child's records are kept after they leave, before permanent deletion is due.</p>
                                        </div>
                                    </div>

                                    {isSupportSession && (
                                        <div className="alert alert-light border py-2" style={{ fontSize: '0.85rem' }}>
                                            <i className="fas fa-lock me-2" aria-hidden="true" />
                                            Data retention is the organisation's decision, so support can view it but not change it.
                                        </div>
                                    )}
                                    <fieldset disabled={isSupportSession}>
                                    <div className="mb-4">
                                        <label className="form-label d-block">Keep a leaver's records until</label>
                                        <div className="form-check">
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                id="retentionAnchorLeaving"
                                                name="retentionAnchor"
                                                checked={orgRetentionAnchor === 'leaving'}
                                                onChange={() => setOrgRetentionAnchor('leaving')}
                                            />
                                            <label className="form-check-label" htmlFor="retentionAnchorLeaving">
                                                A number of years after the child leaves
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                id="retentionAnchorDob25"
                                                name="retentionAnchor"
                                                checked={orgRetentionAnchor === 'dob25'}
                                                onChange={() => setOrgRetentionAnchor('dob25')}
                                            />
                                            <label className="form-check-label" htmlFor="retentionAnchorDob25">
                                                The child's 25th birthday
                                            </label>
                                        </div>
                                        <small className="text-muted">
                                            The 25th-birthday option needs a date of birth on the child's profile;
                                            children without one fall back to the years-after-leaving rule below.
                                            The date is set when a child is marked as left, and admins can adjust
                                            it per child from the Leavers screen.
                                        </small>
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label">Years kept after leaving</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            style={{ maxWidth: 120 }}
                                            min={0}
                                            max={30}
                                            value={orgRetentionYears}
                                            onChange={e => setOrgRetentionYears(Math.max(0, Math.min(30, Number(e.target.value) || 0)))}
                                        />
                                        <small className="text-muted">
                                            {orgRetentionAnchor === 'dob25'
                                                ? 'Used only for children without a date of birth on their profile.'
                                                : 'Applies to every child marked as left from now on.'}
                                        </small>
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label">Deletion reminder emails</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={orgDeletionNotifyEmails}
                                            onChange={e => setOrgDeletionNotifyEmails(e.target.value)}
                                            placeholder="e.g. dpo@example.org, office@example.org"
                                        />
                                        <small className="text-muted">
                                            Comma-separated. Reminders are sent 30 days and 7 days before a record's
                                            deletion date, on the date itself, then monthly until actioned. Leave
                                            blank to send reminders to all administrators. Reminder emails never
                                            contain children's names.
                                        </small>
                                    </div>

                                    <div className="alert alert-light border" style={{ fontSize: '0.85rem' }}>
                                        <strong>Setting your retention period.</strong> Your organisation is the data
                                        controller, so the retention period is your policy decision — Rustle It only
                                        enforces it. For SEND settings in England, sector guidance (the IRMS toolkit
                                        for schools) recommends keeping SEN pupil records until the child's
                                        <strong> 25th birthday</strong>. Records with safeguarding relevance may need
                                        longer — you can extend an individual child's date from the Leavers screen.
                                        Nothing is ever deleted automatically: an administrator must review and
                                        confirm every permanent deletion, and each one is recorded in the deletion log.
                                    </div>
                                    </fieldset>
                                </section>
                            </form>

                            {/* Outside the form: support-access decisions take effect at
                                once and are not part of Save Changes. */}
                            {!isSupportSession && <div className="org-settings__form"><section className="org-section" ref={orgSupportRef} aria-labelledby="org-sec-support">
                                <div className="org-section__head">
                                    <div className="org-section__head-icon"><i className="fas fa-life-ring" aria-hidden="true" /></div>
                                    <div>
                                        <h3 id="org-sec-support">Support Access</h3>
                                        <p>Let Rustle It Support in temporarily to help. Changes here take effect immediately.</p>
                                    </div>
                                </div>
                                <SupportAccessPanel onLoaded={() => setSupportPanelLoaded(true)} />
                            </section></div>}
                        </div>

                        {/* Sticky action bar — Save stays reachable from any section, and
                            the save result shows inline beside it (rather than a top alert
                            the user has scrolled past). */}
                        <div className="org-settings__footer">
                            <div className="org-settings__footer-inner">
                                {orgStatusMessage && (
                                    <span
                                        className={`org-save-status org-save-status--${orgStatusMessage.type}`}
                                        role="status"
                                    >
                                        <i className={`fas ${orgStatusMessage.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} me-1`} aria-hidden="true" />
                                        {orgStatusMessage.text}
                                    </span>
                                )}
                                {/* Same pill, same place, as the post editor: the only sign
                                    this form had unsaved work was the user's own memory of
                                    having typed — and a logo picked here is invisible until
                                    saved, so it is the easiest thing in the app to walk away
                                    from. */}
                                {isOrgDirty() && !isSavingOrg && (
                                    <span className="unsaved-chip" role="status">
                                        <i className="fas fa-circle unsaved-chip__dot" aria-hidden="true" />
                                        Unsaved changes
                                    </span>
                                )}
                                <button
                                    type="submit"
                                    form="org-settings-form"
                                    className="btn btn-primary"
                                    data-help="admin.org-save"
                                    // Greyed out with nothing to write, as the post editor's
                                    // Save is. The pill beside it carries the other half of
                                    // the message: enabled means there is something pending.
                                    disabled={isSavingOrg || !isOrgDirty()}
                                    title={!isSavingOrg && !isOrgDirty() ? 'No changes to save' : undefined}
                                >
                                    {isSavingOrg ? 'Saving…' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                )}

                {activeTab === 'leavers' && (
                    <LeaversPanel onChanged={() => { fetchChildProfiles(); refreshLeaverCount(); }} />
                )}

                {activeTab === 'reportTemplates' && <ReportTemplateBuilder />}

                </div>{/* end content area */}

            <ConfirmationModal
                show={showDiscardModal}
                title="Unsaved Changes"
                message="You have unsaved changes. Do you want to discard them?"
                onConfirm={handleDiscardConfirm}
                onCancel={handleDiscardCancel}
                confirmText="Discard"
                cancelText="Keep Editing"
            />

            {/* Status change applies on its own, so it confirms first and names the
                consequence. Reactivating is harmless, so it goes through without one. */}
            <ConfirmationModal
                show={!!pendingStatus && !pendingStatus.next}
                title={pendingStatus?.kind === 'staff' ? 'Deactivate this user?' : 'Deactivate this child?'}
                message={pendingStatus?.kind === 'staff'
                    ? 'They lose access to this organisation straight away. The record is kept and you can reactivate them at any time.'
                    : 'They are hidden from posts, schedules and reports straight away. The record is kept and you can reactivate them at any time.'}
                confirmText={statusSaving ? 'Deactivating…' : 'Deactivate'}
                cancelText="Cancel"
                isDeleting
                onConfirm={() => {
                    if (!pendingStatus) return;
                    if (pendingStatus.kind === 'staff') applyStaffActive(false);
                    else applyChildActive(false);
                }}
                onCancel={() => setPendingStatus(null)}
            />

            {showMarkAsLeft && editChild && editChild.id !== 0 && (
                <MarkAsLeftModal
                    child={editChild}
                    onClose={(didDelete) => {
                        setShowMarkAsLeft(false);
                        if (didDelete) {
                            // The child is now a leaver — close the editor and drop
                            // it from the grid (the API no longer returns it here).
                            setEditChild(null);
                            setSelectedChildId(null);
                            clearImageState();
                            fetchChildProfiles();
                            // First leaver → the Leavers tab appears now, not on
                            // the next visit to Admin.
                            refreshLeaverCount();
                        }
                    }}
                />
            )}
        </>
    );
};

export default Admin;
